import type { Decor, Rect } from '../world/mapdef';
import { hasGame, S } from '../core/game';
import { block, ellipse, rect, shadow, shade, sun, type Ctx } from './paint';
import { wind } from './nature';
import { registerPropDrawers } from './propRegistry';
import { drawCharacter, type CharLook, type Facing } from './sprites';

/**
 * Prop drawers of the cks package: Liberty Square / CKS Memorial Hall and Beitou hot springs.
 * Big buildings are pre-rendered once per day/night look into bitmaps (`cachedArt`) and blitted;
 * the Memorial Hall is split into three slices that share one picture (see maps/cks.ts).
 */

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';

// ---------------------------------------------------------------------------
// Helpers (same look as props4 / propsHualien)
// ---------------------------------------------------------------------------

function txt(c: Ctx, s: string, x: number, y: number, size: number, col: string, weight = 900, shadowCol?: string): void {
  c.font = `${weight} ${size}px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  if (shadowCol) {
    c.fillStyle = shadowCol;
    c.fillText(s, x + 0.4, y + 0.5);
  }
  c.fillStyle = col;
  c.fillText(s, x, y);
}

function vgrad(c: Ctx, x: number, y: number, w: number, h: number, top: string, bottom: string): void {
  const g = c.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  c.fillStyle = g;
  c.fillRect(x, y, w, h);
}

function poly(c: Ctx, col: string, pts: number[]): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.closePath();
  c.fill();
}

function line(c: Ctx, col: string, w: number, pts: number[]): void {
  c.strokeStyle = col;
  c.lineWidth = w;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.stroke();
}

const isNight = () => sun.alpha < 0.8;
const now = () => (hasGame() ? S().time : 12 * 60);

const bitmaps = new Map<string, { cv: HTMLCanvasElement; s: number }>();
/** Pre-render a static picture covering (x0, y0, w, h) once, then blit it (optionally clipped). */
function cachedArt(c: Ctx, key: string, x0: number, y0: number, w: number, h: number, draw: (cc: Ctx) => void): void {
  const m = c.getTransform();
  const s = Math.min(3, Math.max(1, Math.round(Math.hypot(m.a, m.b) * 2) / 2));
  let e = bitmaps.get(key);
  if (!e || e.s !== s) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil(h * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(-x0, -y0);
    draw(cc);
    e = { cv, s };
    bitmaps.set(key, e);
  }
  c.drawImage(e.cv, x0, y0, w, h);
}

/** A signboard on two posts with a title and a small English line. */
function board(c: Ctx, cx: number, by: number, w: number, title: string, sub: string, bg = '#1f3a6e', fg = '#fff6dc'): void {
  shadow(c, cx, by, w / 2, 2.2);
  for (const px of [cx - w / 2 + 3, cx + w / 2 - 5]) block(c, px, by - 16, 2.2, 16, '#5a4a3a', '#7a6a58');
  block(c, cx - w / 2, by - 30, w, 15, bg, shade(bg, 0.2), shade(bg, -0.3));
  rect(c, 'rgba(255,255,255,0.25)', cx - w / 2 + 1.5, by - 28.6, w - 3, 0.5);
  txt(c, title, cx, by - 24.4, 5.6, fg, 900, 'rgba(0,0,0,0.35)');
  txt(c, sub, cx, by - 18.8, 2.4, shade(fg, -0.15), 700);
}

/** A cheerful little stall cart with a striped umbrella-awning. */
function cart(c: Ctx, r: Rect, t: number, label: string, en: string, canopy: string, goods: (c: Ctx, x: number, y: number, t: number) => void): void {
  const { x, y, w, h } = r;
  const by = y + h;
  shadow(c, x + w / 2, by, w / 2 + 2, 3);
  // Wheels and body.
  for (const wx of [x + 6, x + w - 6]) {
    ellipse(c, '#2b2e33', wx, by - 2, 3.2, 3.2);
    ellipse(c, '#8a9096', wx, by - 2, 1.4, 1.4);
  }
  block(c, x, by - 20, w, 16, '#f4f1ea', '#ffffff', '#c8c4b8');
  rect(c, canopy, x + 1, by - 13, w - 2, 2);
  txt(c, label, x + w / 2, by - 8.6, 4.6, shade(canopy, -0.35), 900);
  txt(c, en, x + w / 2, by - 5, 2, '#55606a', 700);
  // Posts and striped canopy.
  rect(c, '#55606a', x + 2, by - 44, 1.4, 24);
  rect(c, '#55606a', x + w - 3.4, by - 44, 1.4, 24);
  const top = by - 50;
  for (let i = 0; i < w + 4; i += 6) {
    c.fillStyle = (i / 6) % 2 ? '#fff6e6' : canopy;
    c.fillRect(x - 2 + i, top, Math.min(6, w + 4 - i), 7);
    c.beginPath();
    c.arc(x + 1 + i, top + 7, 3, 0, Math.PI);
    c.fill();
  }
  rect(c, 'rgba(0,0,0,0.22)', x - 2, top + 7, w + 4, 1);
  goods(c, x, by - 20, t);
  if (isNight()) {
    ellipse(c, '#fff3b0', x + w / 2, top + 12, 1.6, 1.6);
    ellipse(c, 'rgba(255,230,140,0.22)', x + w / 2, top + 16, 14, 9);
  }
}

function steam(c: Ctx, x: number, y: number, t: number, n = 3, a = 0.35, rise = 12): void {
  for (let i = 0; i < n; i++) {
    const k = (t * 0.5 + i / n) % 1;
    ellipse(c, `rgba(255,255,255,${a * (1 - k)})`, x + Math.sin(t * 1.6 + i * 2) * 2 + k * 3, y - k * rise, 1.5 + k * 3, 1.2 + k * 2);
  }
}

/** A person from the character renderer, at a fixed pose. */
function person(c: Ctx, look: CharLook, x: number, y: number, facing: Facing, t: number, moving = false, phase = 0, z = 0): void {
  drawCharacter(c, look, x, y, { facing, phase: moving ? phase % 1 : 0, moving, blink: Math.sin(t * 1.3 + x) > 0.97, breath: (t * 0.4 + x * 0.01) % 1, z });
}

const L = {
  white: '#f4f2ec',
  whiteLight: '#ffffff',
  whiteShade: '#d6d2c8',
  blue: '#1f4fa8',
  blueLight: '#3a74d0',
  blueDark: '#15357a',
  gold: '#e8c048',
  goldDark: '#b88a2a',
  red: '#c0392b',
  bracket: '#2f8a7a',
};

// ---------------------------------------------------------------------------
// Liberty Square Gate (自由廣場牌樓): five openings, white piers, blue roofs
// ---------------------------------------------------------------------------

/** Hipped blue roof with upturned corners over one bay. `w` is the eave width. */
function blueRoof(c: Ctx, cx: number, yb: number, w: number, h: number, gold = false): void {
  const hw = w / 2;
  // Bracket band under the eave.
  block(c, cx - hw + 4, yb, w - 8, 4, L.bracket, '#4aa898', '#1f6a5a');
  for (let i = cx - hw + 7; i < cx + hw - 6; i += 4) rect(c, i % 8 < 4 ? L.gold : '#c0392b', i, yb + 1.2, 1.2, 1.4);
  // Roof body with upswept tips.
  c.fillStyle = L.blueDark;
  c.beginPath();
  c.moveTo(cx - hw - 4, yb - 3);
  c.quadraticCurveTo(cx - hw + 2, yb + 1, cx - hw + 10, yb);
  c.lineTo(cx + hw - 10, yb);
  c.quadraticCurveTo(cx + hw - 2, yb + 1, cx + hw + 4, yb - 3);
  c.lineTo(cx + hw - 8, yb - h);
  c.lineTo(cx - hw + 8, yb - h);
  c.closePath();
  c.fill();
  c.fillStyle = L.blue;
  c.beginPath();
  c.moveTo(cx - hw - 2, yb - 3.6);
  c.quadraticCurveTo(cx - hw + 3, yb - 0.6, cx - hw + 10, yb - 1.2);
  c.lineTo(cx + hw - 10, yb - 1.2);
  c.quadraticCurveTo(cx + hw - 3, yb - 0.6, cx + hw + 2, yb - 3.6);
  c.lineTo(cx + hw - 9, yb - h + 1);
  c.lineTo(cx - hw + 9, yb - h + 1);
  c.closePath();
  c.fill();
  // Tile rows.
  for (let i = cx - hw + 10; i < cx + hw - 9; i += 3) {
    const k = (i - cx) / hw;
    line(c, 'rgba(10,30,80,0.45)', 0.5, [i, yb - 1.4, i - k * 2, yb - h + 1.4]);
  }
  rect(c, 'rgba(160,200,255,0.4)', cx - hw + 10, yb - h + 2, w - 20, 0.8);
  // Ridge with small upturned ends.
  block(c, cx - hw + 7, yb - h - 3, w - 14, 4, gold ? L.gold : L.blueDark, gold ? '#f6dc80' : L.blueLight);
  poly(c, gold ? L.goldDark : L.blueDark, [cx - hw + 7, yb - h - 3, cx - hw + 4, yb - h - 7, cx - hw + 9, yb - h + 1]);
  poly(c, gold ? L.goldDark : L.blueDark, [cx + hw - 7, yb - h - 3, cx + hw - 4, yb - h - 7, cx + hw - 9, yb - h + 1]);
}

function gateArt(c: Ctx, cx: number, by: number, night: boolean): void {
  const W = 280;
  const x0 = cx - W / 2;
  // Openings: [width, height, roof bottom, roof height]
  const bays: [number, number, number, number][] = [[26, 34, 50, 13], [34, 44, 62, 15], [44, 58, 80, 20], [34, 44, 62, 15], [26, 34, 50, 13]];
  const P = 12, END = 22;
  shadow(c, cx, by, W / 2 + 6, 5, 0.3, 40);
  // Stone base and three shallow steps.
  block(c, x0 - 6, by - 6, W + 12, 6, '#c8c4bc', '#e0dcd4', '#9a968e');
  rect(c, '#b0aca4', x0 - 2, by - 3, W + 4, 0.6);
  // End walls (low) with a blue tile cap.
  for (const ex of [x0, cx + W / 2 - END]) {
    block(c, ex, by - 40, END, 34, L.white, L.whiteLight, L.whiteShade);
    rect(c, L.blueDark, ex - 1, by - 44, END + 2, 5);
    rect(c, L.blueLight, ex - 1, by - 44, END + 2, 1);
    // A round lattice window.
    ellipse(c, '#9aa4ae', ex + END / 2, by - 24, 6, 6);
    ellipse(c, L.white, ex + END / 2, by - 24, 4.6, 4.6);
    for (let k = -1; k <= 1; k++) {
      rect(c, '#2f8a7a', ex + END / 2 - 4, by - 24 + k * 2.4 - 0.3, 8, 0.6);
      rect(c, '#2f8a7a', ex + END / 2 + k * 2.4 - 0.3, by - 28, 0.6, 8);
    }
  }
  // Bays: piers, the wall above each opening, then the opening.
  let x = x0 + END;
  const centres: number[] = [];
  for (let i = 0; i < bays.length; i++) {
    const [bw, bh, rb] = bays[i];
    // Left pier of this bay.
    block(c, x, by - rb + 2, P, rb - 8, L.white, L.whiteLight, L.whiteShade);
    rect(c, 'rgba(0,0,0,0.06)', x + P - 2, by - rb + 4, 1.4, rb - 12);
    x += P;
    // Wall over the opening.
    block(c, x - 1, by - rb + 2, bw + 2, rb - bh - 2, L.white, L.whiteLight, L.whiteShade);
    // Opening: a pale view through to the street, shaded at the top.
    vgrad(c, x, by - bh - 6, bw, bh, night ? '#1a2230' : '#7f909e', night ? '#3a4250' : '#d6d8d2');
    rect(c, 'rgba(0,0,0,0.18)', x, by - bh - 6, bw, 3);
    rect(c, L.whiteShade, x - 1, by - bh - 7, bw + 2, 1.2);
    centres.push(x + bw / 2);
    x += bw;
  }
  // Last pier.
  block(c, x, by - 50 + 2, P, 50 - 8, L.white, L.whiteLight, L.whiteShade);
  // Roofs, lowest first so the tall central one overlaps.
  const order = [0, 4, 1, 3, 2];
  for (const i of order) {
    const [bw, , rb, rh] = bays[i];
    blueRoof(c, centres[i], by - rb, bw + P * 2 + 8, rh, false);
  }
  // The name plaque: 自由廣場 in dark blue on white, framed in gold, under the central roof.
  const pc = centres[2];
  block(c, pc - 22, by - 77 + 4, 44, 11, L.goldDark, L.gold);
  rect(c, '#fbfaf6', pc - 20.6, by - 77 + 5.4, 41.2, 8.2);
  txt(c, '自由廣場', pc, by - 77 + 9.6, 6.6, '#1f2f5e', 900);
  if (night) {
    // Uplights at the foot of the piers.
    for (const px of centres) {
      ellipse(c, 'rgba(255,248,220,0.5)', px - 24, by - 7, 3, 1.2);
      ellipse(c, 'rgba(255,248,220,0.5)', px + 24, by - 7, 3, 1.2);
    }
  }
}

// ---------------------------------------------------------------------------
// The National Theater and the National Concert Hall: palace halls, golden roofs, red pillars
// ---------------------------------------------------------------------------

function palaceArt(c: Ctx, cx: number, by: number, concert: boolean, night: boolean): void {
  const W = 320;
  const x0 = cx - W / 2;
  const roof = '#e8a828', roofLight = '#f8c850', roofDark = '#b87a18';
  shadow(c, cx, by, W / 2 + 8, 5, 0.3, 50);
  // --- Marble platform, balustrade and the broad front steps -----------------------------------
  block(c, x0 - 8, by - 18, W + 16, 18, L.white, L.whiteLight, '#b8b2a6');
  for (let i = 0; i < 3; i++) rect(c, 'rgba(0,0,0,0.08)', x0 - 6, by - 14 + i * 5, W + 12, 0.6);
  // Steps (centre): bright treads.
  for (let i = 0; i < 6; i++) block(c, cx - 50 + i * 2, by - 3 - i * 3, 100 - i * 4, 3.4, '#f6f4ee', '#ffffff', '#cfcac0');
  // Balustrade.
  for (const [a, b2] of [[x0 - 6, cx - 52], [cx + 52, x0 + W + 6]]) {
    rect(c, L.whiteShade, a, by - 24, b2 - a, 1.4);
    for (let i = a; i < b2; i += 6) block(c, i, by - 24, 2, 6, L.white, L.whiteLight);
    rect(c, L.white, a, by - 25.4, b2 - a, 1.6);
  }
  // --- Colonnade -------------------------------------------------------------------------------
  const top = by - 66;
  // Back wall with doors (lit at night).
  vgrad(c, x0 + 6, top + 6, W - 12, 42, '#6a2a22', '#4a1c16');
  const doors = 7;
  for (let i = 0; i < doors; i++) {
    const dx = x0 + 22 + i * ((W - 44) / (doors - 1)) - 8;
    block(c, dx, top + 18, 16, 30, night ? '#ffd890' : '#8a3a2a', night ? '#fff0c0' : '#a8503a', '#4a1c16');
    for (let k = 1; k < 4; k++) rect(c, night ? 'rgba(160,80,20,0.4)' : 'rgba(30,10,6,0.35)', dx + k * 4, top + 19, 0.5, 28);
    rect(c, night ? 'rgba(160,80,20,0.4)' : 'rgba(30,10,6,0.35)', dx + 1, top + 30, 14, 0.5);
  }
  // Red pillars with white bases.
  const N = 12;
  for (let i = 0; i < N; i++) {
    const px = x0 + 10 + i * ((W - 26) / (N - 1));
    block(c, px, top + 4, 6, 44, '#b8322a', '#d8503e', '#8a1f18');
    block(c, px - 1.4, top + 44, 8.8, 4, L.white, L.whiteLight, L.whiteShade);
    rect(c, L.gold, px - 0.6, top + 4, 7.2, 1.4);
  }
  // Painted beam and brackets (blue-green with gold).
  block(c, x0 + 2, top - 4, W - 4, 8, '#2a5aa8', '#3a74d0', '#1f3a6e');
  for (let i = x0 + 6; i < x0 + W - 6; i += 8) {
    rect(c, '#2f8a7a', i, top - 3, 5, 2.4);
    rect(c, L.gold, i + 1.6, top + 0.4, 1.8, 1.8);
  }
  rect(c, L.gold, x0 + 2, top + 3, W - 4, 0.8);
  // --- Lower eave --------------------------------------------------------------------------------
  const e1 = top - 4;
  c.fillStyle = roofDark;
  c.beginPath();
  c.moveTo(x0 - 18, e1 - 6);
  c.quadraticCurveTo(x0 - 6, e1 + 2, x0 + 14, e1);
  c.lineTo(x0 + W - 14, e1);
  c.quadraticCurveTo(x0 + W + 6, e1 + 2, x0 + W + 18, e1 - 6);
  c.lineTo(x0 + W - 4, e1 - 18);
  c.lineTo(x0 + 4, e1 - 18);
  c.closePath();
  c.fill();
  c.fillStyle = roof;
  c.beginPath();
  c.moveTo(x0 - 15, e1 - 7);
  c.quadraticCurveTo(x0 - 4, e1, x0 + 14, e1 - 1.6);
  c.lineTo(x0 + W - 14, e1 - 1.6);
  c.quadraticCurveTo(x0 + W + 4, e1, x0 + W + 15, e1 - 7);
  c.lineTo(x0 + W - 5, e1 - 17);
  c.lineTo(x0 + 5, e1 - 17);
  c.closePath();
  c.fill();
  for (let i = x0 + 8; i < x0 + W - 6; i += 3.4) line(c, 'rgba(120,70,10,0.4)', 0.5, [i, e1 - 2, i + (i - cx) * 0.02, e1 - 16]);
  rect(c, roofLight, x0 + 6, e1 - 17, W - 12, 1);
  // --- Upper wall band with the name plaque -------------------------------------------------------
  const u = e1 - 17;
  block(c, x0 + 26, u - 12, W - 52, 12, '#b8322a', '#d8503e', '#8a1f18');
  for (let i = x0 + 30; i < x0 + W - 30; i += 7) {
    rect(c, '#2f8a7a', i, u - 11, 4.4, 3);
    rect(c, L.gold, i + 1.4, u - 10.4, 1.6, 1.6);
  }
  block(c, cx - 30, u - 12, 60, 11, '#1f3a6e', '#2a4f90');
  rect(c, L.gold, cx - 29, u - 11.2, 58, 0.7);
  rect(c, L.gold, cx - 29, u - 2.2, 58, 0.7);
  txt(c, concert ? '國家音樂廳' : '國家戲劇院', cx, u - 6.4, 6.2, L.gold, 900);
  // --- Upper roof (hip roof seen from above the front) ------------------------------------------
  const e2 = u - 12;
  const rh = 52;
  c.fillStyle = roofDark;
  c.beginPath();
  c.moveTo(x0 - 6, e2 - 4);
  c.quadraticCurveTo(x0 + 6, e2 + 3, x0 + 26, e2);
  c.lineTo(x0 + W - 26, e2);
  c.quadraticCurveTo(x0 + W - 6, e2 + 3, x0 + W + 6, e2 - 4);
  c.lineTo(x0 + W - 58, e2 - rh);
  c.lineTo(x0 + 58, e2 - rh);
  c.closePath();
  c.fill();
  // Front slope (bright) and the two side hips (shaded).
  poly(c, roof, [x0 + 2, e2 - 4.6, x0 + 26, e2 - 1.6, x0 + W - 26, e2 - 1.6, x0 + W - 2, e2 - 4.6, x0 + W - 58, e2 - rh + 2, x0 + 58, e2 - rh + 2]);
  poly(c, shade(roof, -0.12), [x0 + 2, e2 - 4.6, x0 + 58, e2 - rh + 2, x0 + 40, e2 - rh * 0.55]);
  poly(c, shade(roof, -0.12), [x0 + W - 2, e2 - 4.6, x0 + W - 58, e2 - rh + 2, x0 + W - 40, e2 - rh * 0.55]);
  if (concert) {
    // Hip-and-gable: a red gable triangle at each end of the ridge.
    poly(c, '#b8322a', [x0 + 58, e2 - rh + 2, x0 + 76, e2 - rh + 2, x0 + 64, e2 - rh + 18]);
    poly(c, '#b8322a', [x0 + W - 58, e2 - rh + 2, x0 + W - 76, e2 - rh + 2, x0 + W - 64, e2 - rh + 18]);
    rect(c, L.gold, x0 + 63, e2 - rh + 8, 2, 2);
    rect(c, L.gold, x0 + W - 65, e2 - rh + 8, 2, 2);
  }
  for (let i = x0 + 30; i < x0 + W - 28; i += 4) {
    const k = (i - cx) / (W / 2);
    line(c, 'rgba(120,70,10,0.38)', 0.55, [i, e2 - 2.4, i - k * 26, e2 - rh + 3]);
  }
  // Tile courses.
  for (let j = 1; j < 4; j++) {
    const yy = e2 - j * (rh / 4);
    const inset = 26 + j * 8;
    rect(c, 'rgba(255,230,150,0.35)', x0 + inset, yy, W - inset * 2, 0.6);
  }
  // Main ridge with dragon-tail ends (chiwen).
  block(c, x0 + 54, e2 - rh - 4, W - 108, 6, roofDark, roofLight);
  for (const [ex, dir] of [[x0 + 54, -1], [x0 + W - 54, 1]] as const) {
    poly(c, roofDark, [ex, e2 - rh - 4, ex + dir * 6, e2 - rh - 14, ex + dir * 9, e2 - rh - 12, ex + dir * 5, e2 - rh + 2]);
    rect(c, L.gold, ex + dir * 6 - 1, e2 - rh - 13, 2, 2);
  }
  // Hip ridges.
  line(c, roofDark, 2, [x0 + 2, e2 - 5, x0 + 56, e2 - rh]);
  line(c, roofDark, 2, [x0 + W - 2, e2 - 5, x0 + W - 56, e2 - rh]);
  // Little roof guardians on the hips.
  for (let k = 1; k <= 3; k++) {
    ellipse(c, roofDark, x0 + 2 + k * 9, e2 - 5 - k * 8.4, 1.4, 1.8);
    ellipse(c, roofDark, x0 + W - 2 - k * 9, e2 - 5 - k * 8.4, 1.4, 1.8);
  }
  if (night) {
    ellipse(c, 'rgba(255,220,140,0.14)', cx, top + 30, W / 2, 30);
  }
}

// ---------------------------------------------------------------------------
// The National Chiang Kai-shek Memorial Hall
// ---------------------------------------------------------------------------

// Geometry in map pixels (see maps/cks.ts): platform x 1216..1520, y 320..640; body square
// x 1240..1496, y 352..608; centre (1368, 480). Heights are projected straight up the screen.
const HC = { cx: 1368, cy: 480, px0: 1216, px1: 1520, py0: 320, py1: 640, plat: 34, bx0: 1240, bx1: 1496, by0: 352, by1: 608, wall: 140 };
const HALL_BOX = { x0: 1200, y0: 84, w: 336, h: 562 };
/** Roof heights (z) and half-widths of the flat sides. */
const RF = { fas1: 128, lip1: 134, flat1: 146, inFlat: 104, in1: 164, drumTop: 196, fas2: 190, lip2: 196, flat2: 128, apex: 356 };

/** Octagon vertices (flat sides facing the four directions) at height z. `f` = half-width of the flat sides. */
function oct(f: number, z: number, cx = HC.cx, cy = HC.cy): number[] {
  const r = f / Math.cos(Math.PI / 8);
  const pts: number[] = [];
  for (let k = 0; k < 8; k++) {
    const a = Math.PI / 8 + (k * Math.PI) / 4;
    pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r - z);
  }
  return pts;
}

function hallBase(c: Ctx, night: boolean): void {
  const { px0, px1, py0, py1, plat, bx0, bx1, by1, wall } = HC;
  // Shadow on the ground.
  ellipse(c, 'rgba(20,10,6,0.18)', (px0 + px1) / 2 + 6, py1 + 1, (px1 - px0) / 2 + 8, 6);
  // Platform top: pale marble with a grid.
  poly(c, '#ebe7dc', [px0, py0 - plat, px1, py0 - plat, px1, py1 - plat, px0, py1 - plat]);
  for (let x = px0 + 16; x < px1; x += 16) rect(c, 'rgba(150,140,120,0.16)', x, py0 - plat, 0.5, py1 - py0);
  for (let y = py0 - plat + 16; y < py1 - plat; y += 16) rect(c, 'rgba(150,140,120,0.16)', px0, y, px1 - px0, 0.5);
  // Front face of the platform: three tiers, blue-grey plinth at the bottom.
  vgrad(c, px0, py1 - plat, px1 - px0, plat, '#f6f3ec', '#dcd6ca');
  rect(c, '#c8c2b6', px0, py1 - plat + 11, px1 - px0, 1);
  rect(c, '#c8c2b6', px0, py1 - plat + 22, px1 - px0, 1);
  for (let x = px0 + 12; x < px1; x += 24) {
    rect(c, 'rgba(0,0,0,0.06)', x, py1 - plat + 1, 0.6, 10);
    rect(c, 'rgba(0,0,0,0.06)', x + 12, py1 - plat + 12, 0.6, 10);
  }
  rect(c, '#7a8796', px0, py1 - 5, px1 - px0, 5);
  rect(c, '#9aa6b4', px0, py1 - 5, px1 - px0, 1);
  // Balustrades round the platform edge (front, west except where the stairs land, east).
  const rail = (x0: number, y0: number, x1: number, y1: number) => {
    const n = Math.max(2, Math.round(Math.hypot(x1 - x0, y1 - y0) / 7));
    for (let i = 0; i <= n; i++) {
      const x = x0 + ((x1 - x0) * i) / n, y = y0 + ((y1 - y0) * i) / n;
      block(c, x - 1, y - 6, 2.2, 6, '#ffffff', '#ffffff', '#cfc8bb');
    }
    line(c, '#ffffff', 1.6, [x0, y0 - 6, x1, y1 - 6]);
    line(c, '#cfc8bb', 0.5, [x0, y0 - 4.8, x1, y1 - 4.8]);
  };
  rail(px0 + 2, py0 - plat + 4, px0 + 2, 382 + 4);
  rail(px0 + 2, 510 + 2, px0 + 2, py1 - plat);
  rail(px1 - 2, py0 - plat + 4, px1 - 2, py1 - plat);
  rail(px0 + 2, py0 - plat + 4, px1 - 2, py0 - plat + 4);
  // --- The body's south wall (white marble) --------------------------------------------------------
  const wy1 = by1 - plat, wy0 = by1 - wall;
  vgrad(c, bx0, wy0, bx1 - bx0, wy1 - wy0, '#fbfaf6', '#e6e1d6');
  rect(c, '#8a96a4', bx0, wy1 - 8, bx1 - bx0, 8);
  rect(c, '#aab4c0', bx0, wy1 - 8, bx1 - bx0, 1);
  // Corner pilasters.
  for (const x of [bx0, bx1 - 12]) block(c, x, wy0, 12, wy1 - wy0 - 8, '#f0ece2', '#ffffff', '#d6d0c4');
  // The great doorway with its blue plaque.
  const dcx = HC.cx;
  block(c, dcx - 22, wy1 - 66, 44, 58, '#d8d2c4', '#ece8de');
  vgrad(c, dcx - 18, wy1 - 62, 36, 54, night ? '#ffe2a0' : '#2c4a46', night ? '#e8b060' : '#1a2e2c');
  if (!night) {
    for (let k = 0; k < 4; k++) for (let j = 0; j < 6; j++) ellipse(c, '#c8a048', dcx - 13.5 + k * 9, wy1 - 56 + j * 8, 0.8, 0.8);
    rect(c, '#c8a048', dcx - 0.4, wy1 - 62, 0.8, 54);
  }
  block(c, dcx - 26, wy1 - 80, 52, 11, '#1f3a6e', '#2a4f90');
  rect(c, L.gold, dcx - 25, wy1 - 79.2, 50, 0.6);
  txt(c, '中正紀念堂', dcx, wy1 - 74.4, 6.6, L.gold, 900);
  // Tall recessed windows with blue lattice either side.
  for (const off of [-100, -74, -48, 48, 74, 100]) {
    const wx = dcx + off - 7;
    block(c, wx - 1, wy1 - 62, 16, 46, '#d6d0c4', '#e6e0d4');
    vgrad(c, wx + 1, wy1 - 60, 12, 42, night ? '#ffe2a0' : '#3a5a8a', night ? '#d89a50' : '#1f3a6e');
    for (let j = 1; j < 6; j++) rect(c, 'rgba(255,255,255,0.35)', wx + 1, wy1 - 60 + j * 7, 12, 0.5);
    rect(c, 'rgba(255,255,255,0.35)', wx + 6.6, wy1 - 60, 0.6, 42);
  }
  if (night) ellipse(c, 'rgba(255,250,235,0.12)', HC.cx, wy0 + 30, 130, 60);
}

function hallRoof(c: Ctx): void {
  const { fas1, lip1, flat1, inFlat, in1, drumTop, fas2, lip2, flat2, apex } = RF;
  // --- Lower eave ring -------------------------------------------------------------------------
  poly(c, '#163a2e', oct(flat1 - 2, fas1 - 1));
  poly(c, L.bracket, oct(flat1 - 4, fas1));
  // Bracket dots along the visible (southern) edges.
  const lo = oct(flat1 - 4, fas1 + 3);
  for (let k = 0; k < 4; k++) {
    const ax = lo[k * 2], ay = lo[k * 2 + 1], bx = lo[((k + 1) % 8) * 2], by2 = lo[((k + 1) % 8) * 2 + 1];
    for (let i = 0; i <= 12; i++) {
      const u = i / 12;
      rect(c, i % 2 ? L.gold : L.red, ax + (bx - ax) * u - 0.7, ay + (by2 - ay) * u - 0.7, 1.4, 1.6);
    }
  }
  poly(c, L.blueDark, oct(flat1, lip1));
  poly(c, L.blue, oct(flat1 - 3, lip1 + 2));
  // Tile rings between the lip and the drum.
  for (let j = 1; j <= 5; j++) {
    const u = j / 6;
    const f = flat1 - 3 + (inFlat - flat1 + 3) * u, z = lip1 + 2 + (in1 - lip1 - 2) * u;
    c.strokeStyle = j % 2 ? 'rgba(10,30,80,0.5)' : 'rgba(150,190,255,0.4)';
    c.lineWidth = 0.7;
    const p = oct(f, z);
    c.beginPath();
    c.moveTo(p[0], p[1]);
    for (let i = 2; i < 16; i += 2) c.lineTo(p[i], p[i + 1]);
    c.closePath();
    c.stroke();
  }
  const outer = oct(flat1 - 3, lip1 + 2), inner = oct(inFlat, in1);
  for (let k = 0; k < 8; k++) line(c, L.blueLight, 1.3, [outer[k * 2], outer[k * 2 + 1], inner[k * 2], inner[k * 2 + 1]]);
  for (let k = 0; k < 8; k++) ellipse(c, L.gold, outer[k * 2], outer[k * 2 + 1] - 1.6, 1.5, 1.5);
  // --- Drum between the eaves: white walls, windows, a bracket frieze --------------------------------
  const db = oct(inFlat, in1), dt = oct(inFlat, drumTop), t1 = oct(inFlat, drumTop - 9);
  for (let k = 0; k < 3; k++) {
    const a = k, b2 = k + 1;
    poly(c, k === 1 ? '#fbfaf6' : '#e6e2d8', [db[a * 2], db[a * 2 + 1], db[b2 * 2], db[b2 * 2 + 1], dt[b2 * 2], dt[b2 * 2 + 1], dt[a * 2], dt[a * 2 + 1]]);
    poly(c, L.bracket, [t1[a * 2], t1[a * 2 + 1], t1[b2 * 2], t1[b2 * 2 + 1], dt[b2 * 2], dt[b2 * 2 + 1], dt[a * 2], dt[a * 2 + 1]]);
    for (let i = 1; i < 12; i++) {
      const u = i / 12;
      const x = t1[a * 2] + (t1[b2 * 2] - t1[a * 2]) * u, y = t1[a * 2 + 1] + (t1[b2 * 2 + 1] - t1[a * 2 + 1]) * u;
      rect(c, i % 2 ? L.gold : '#ffffff', x - 0.6, y + 2.6, 1.2, 3);
    }
    // Two tall windows on each visible face.
    for (const u of [0.3, 0.7]) {
      const mx = db[a * 2] + (db[b2 * 2] - db[a * 2]) * u, my = db[a * 2 + 1] + (db[b2 * 2 + 1] - db[a * 2 + 1]) * u;
      block(c, mx - 4, my - 22, 8, 15, '#2a4f90', '#3a74d0', '#1f3a6e');
      rect(c, 'rgba(255,255,255,0.4)', mx - 0.3, my - 21, 0.6, 13);
    }
  }
  // --- Upper eave and the tall eight-sided pyramid ----------------------------------------------------
  poly(c, '#163a2e', oct(flat2 - 2, fas2 - 1));
  poly(c, L.bracket, oct(flat2 - 4, fas2));
  const lo2 = oct(flat2 - 4, fas2 + 3);
  for (let k = 0; k < 4; k++) {
    const ax = lo2[k * 2], ay = lo2[k * 2 + 1], bx = lo2[((k + 1) % 8) * 2], by2 = lo2[((k + 1) % 8) * 2 + 1];
    for (let i = 0; i <= 10; i++) {
      const u = i / 10;
      rect(c, i % 2 ? L.gold : L.red, ax + (bx - ax) * u - 0.7, ay + (by2 - ay) * u - 0.7, 1.4, 1.6);
    }
  }
  poly(c, L.blueDark, oct(flat2, lip2));
  const base = oct(flat2 - 3, lip2 + 2);
  const ax = HC.cx, ay = HC.cy - apex;
  const facetShade = [0.1, 0.16, 0.1, -0.04, -0.16, -0.22, -0.16, -0.04];
  for (let k = 0; k < 8; k++) {
    const n = (k + 1) % 8;
    poly(c, shade(L.blue, facetShade[k]), [base[k * 2], base[k * 2 + 1], base[n * 2], base[n * 2 + 1], ax, ay]);
  }
  // Slightly concave look: tile courses get closer near the top.
  for (let j = 1; j <= 7; j++) {
    const u = 1 - (1 - j / 8) ** 1.4;
    c.strokeStyle = 'rgba(10,30,80,0.4)';
    c.lineWidth = 0.6;
    c.beginPath();
    for (let k = 0; k <= 8; k++) {
      const i = k % 8;
      const x = base[i * 2] + (ax - base[i * 2]) * u, y = base[i * 2 + 1] + (ay - base[i * 2 + 1]) * u;
      if (k === 0) c.moveTo(x, y);
      else c.lineTo(x, y);
    }
    c.stroke();
  }
  // Vertical tile lines on the three front facets.
  for (let k = 0; k < 3; k++) {
    const n = k + 1;
    for (let i = 1; i < 8; i++) {
      const u = i / 8;
      const bx = base[k * 2] + (base[n * 2] - base[k * 2]) * u, by2 = base[k * 2 + 1] + (base[n * 2 + 1] - base[k * 2 + 1]) * u;
      line(c, 'rgba(150,190,255,0.25)', 0.5, [bx, by2, bx + (ax - bx) * 0.85, by2 + (ay - by2) * 0.85]);
    }
  }
  // Gold ridges from each corner up to the top, with upturned tips.
  for (let k = 0; k < 8; k++) {
    line(c, L.goldDark, 2, [base[k * 2], base[k * 2 + 1], ax, ay]);
    line(c, '#f6dc80', 0.7, [base[k * 2], base[k * 2 + 1] - 0.7, ax, ay - 0.7]);
    ellipse(c, L.gold, base[k * 2], base[k * 2 + 1] - 1.6, 1.8, 1.8);
  }
  // Red top ring and the golden finial (寶頂).
  ellipse(c, '#8a1f18', ax, ay + 1, 10, 4);
  block(c, ax - 9, ay - 7, 18, 8, L.red, '#e05a48', '#8a1f18');
  ellipse(c, '#d8503e', ax, ay - 7, 9, 3);
  ellipse(c, L.goldDark, ax, ay - 12, 6.4, 5.2);
  ellipse(c, L.gold, ax, ay - 13, 5.8, 4.6);
  ellipse(c, L.gold, ax, ay - 20, 3.6, 4);
  ellipse(c, '#fff3b0', ax - 1.4, ay - 21, 1.1, 1.6);
  rect(c, L.goldDark, ax - 0.8, ay - 30, 1.6, 7);
  ellipse(c, L.gold, ax, ay - 30, 1.8, 1.8);
}

const GUARD: CharLook = { skin: '#e8b48c', hair: '#1c1814', hairStyle: 'cap', hat: '#f4f5f2', top: '#f4f5f2', topShade: '#d0d4d8', bottom: '#f4f5f2', shoes: '#1d1814', badge: '#e8c048', tie: '#1f3a6e' };

/** One honour guard on the terrace, with a rifle (spun during the ceremony). */
function guardFig(c: Ctx, x: number, y: number, facing: Facing, t: number, moving: boolean, spin: number): void {
  person(c, GUARD, x, y, facing, t, moving, t * 0.6);
  // Rifle: shouldered, or twirling.
  const a = spin ? t * 9 : -1.25;
  const hx = x + (facing === 'left' ? -4 : 4), hy = y - 14;
  const dx = Math.cos(a) * 9, dy = Math.sin(a) * 9;
  line(c, '#5a3a20', 1.4, [hx - dx * 0.4, hy - dy * 0.4, hx + dx * 0.5, hy + dy * 0.5]);
  line(c, '#9aa4ae', 0.8, [hx + dx * 0.5, hy + dy * 0.5, hx + dx, hy + dy]);
  rect(c, '#e8eef4', hx + dx - 0.4, hy + dy - 0.4, 0.8, 0.8);
}

function hallGuards(c: Ctx, t: number): void {
  const z = HC.plat;
  const tm = now();
  const h = Math.floor(tm / 60), mi = tm % 60;
  const ceremony = h >= 9 && h <= 17 && mi < 15;
  const gy = 628 - z;
  if (tm < 9 * 60 || tm >= 18 * 60) return; // the hall is closed: the guards are inside
  if (!ceremony) {
    // Two guards stand perfectly still, flanking the great door on the front terrace.
    guardFig(c, HC.cx - 34, gy, 'down', 0, false, 0);
    guardFig(c, HC.cx + 34, gy, 'down', 0, false, 0);
    return;
  }
  // The ceremony: a file of guards marches slowly along the terrace, spinning their rifles.
  for (let i = 0; i < 4; i++) {
    const u = (t * 0.05 + i * 0.06) % 2;
    const k = u < 1 ? u : 2 - u;
    const gx = 1262 + k * 210;
    guardFig(c, gx, gy + (i % 2) * 3, u < 1 ? 'right' : 'left', t + i * 0.5, true, Math.sin(t * 0.8 + i) > 0.4 ? 1 : 0);
  }
}

function drawHallSlice(c: Ctx, d: Decor, t: number): void {
  const night = isNight();
  const k = d.variant ?? 0;
  const sliceBottom = HC.py1 - k * 150;
  const { x0, y0, w, h } = HALL_BOX;
  c.save();
  c.beginPath();
  c.rect(x0 - 2, sliceBottom - 150, w + 4, k === 0 ? 158 : 150);
  c.clip();
  cachedArt(c, `ckHallBase:${night ? 'n' : 'd'}`, x0, y0, w, h, (cc) => hallBase(cc, night));
  hallGuards(c, t);
  cachedArt(c, 'ckHallRoof', x0, y0, w, h, (cc) => hallRoof(cc));
  c.restore();
}

/** The 89 steps: a long white flight rising east to the terrace, split by a carved central ramp. */
function stairsArt(c: Ctx): void {
  const x0 = 1024, x1 = 1216, yN = 416, yS = 544, H = HC.plat;
  const z = (x: number) => ((x - x0) / (x1 - x0)) * H;
  // South side face: a long wedge with a stepped top edge.
  const sv: number[] = [x0, yS];
  for (let i = 0; i < 30; i++) {
    const xa = x0 + ((x1 - x0) * i) / 30, xb = x0 + ((x1 - x0) * (i + 1)) / 30;
    sv.push(xa, yS - z(xa) - H / 30, xb, yS - z(xa) - H / 30);
  }
  sv.push(x1, yS);
  poly(c, '#dcd6ca', sv);
  rect(c, '#8a96a4', x0, yS - 3, x1 - x0, 3);
  for (let i = 1; i < 4; i++) line(c, 'rgba(0,0,0,0.08)', 0.6, [x0 + ((x1 - x0) * i) / 4, yS - (H * i) / 4, x1, yS - (H * i) / 4]);
  // Treads: each step a light strip with a soft shadow line where the next riser starts.
  const N = 30;
  const cw = 14; // half-width of the central ramp
  const mid = (yN + yS) / 2;
  for (let i = 0; i < N; i++) {
    const xa = x0 + ((x1 - x0) * i) / N, xb = x0 + ((x1 - x0) * (i + 1)) / N;
    const za = z(xa);
    poly(c, '#f2efe8', [xa, yN - za, xb, yN - za, xb, yS - za, xa, yS - za]);
    rect(c, 'rgba(110,100,85,0.28)', xb - 0.6, yN - za - 0.2, 0.6, yS - yN);
    // Step edge on the side face.
    rect(c, '#c8c2b6', xa, yS - za - 0.6, xb - xa, 0.6);
  }
  // The carved central ramp (a smooth sloping band with a cloud pattern).
  poly(c, '#d8d2c4', [x0, mid - cw, x1, mid - cw - H, x1, mid + cw - H, x0, mid + cw]);
  for (let i = 0; i < 9; i++) {
    const x = x0 + 10 + i * 20, zz = z(x);
    ellipse(c, 'rgba(90,110,140,0.35)', x, mid - zz, 5, 3);
    ellipse(c, '#d8d2c4', x + 1.6, mid - zz - 0.6, 3, 1.8);
  }
  // Low balustrades along the outer edges and both sides of the ramp.
  for (const ya of [yN + 2, yS - 1, mid - cw, mid + cw]) {
    for (let i = 0; i <= 12; i++) {
      const x = x0 + ((x1 - x0) * i) / 12;
      rect(c, '#ffffff', x - 0.8, ya - z(x) - 4, 1.6, 4);
      rect(c, 'rgba(0,0,0,0.12)', x + 0.4, ya - z(x) - 4, 0.4, 4);
    }
    line(c, '#ffffff', 1.4, [x0, ya - 4, x1, ya - H - 4]);
    line(c, '#bdb6a8', 0.5, [x0, ya - 2.8, x1, ya - H - 2.8]);
  }
  // Bronze lamp posts at the foot.
  for (const yy of [yN + 4, yS - 2]) {
    block(c, x0 - 5, yy - 18, 3, 18, '#4a4e54', '#6a6e74');
    ellipse(c, '#fff3d0', x0 - 3.5, yy - 20, 2.6, 2.6);
  }
}

const CLIMBERS: CharLook[] = [
  { skin: '#f8dcc4', hair: '#e8c25e', hairStyle: 'pony', top: '#ff7a7a', topShade: '#d95a5a', bottom: '#5a6f8a', shoes: '#f4f1ea' },
  { skin: '#e8b48c', hair: '#141218', hairStyle: 'short', top: '#3a8fd9', topShade: '#2a6fb0', bottom: '#2a2f3d', shoes: '#1d1814' },
  { skin: '#f3cba6', hair: '#3a2418', hairStyle: 'long', top: '#ffd34e', topShade: '#d9a82a', bottom: '#2a2f3d', shoes: '#f4f1ea' },
  { skin: '#d9a47a', hair: '#2a1d16', hairStyle: 'cap', hat: '#5fbf5a', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#6b7a5a', shoes: '#5a3a20' },
];

/** Visitors slowly climbing (and coming down) the 89 steps, daytime. */
function climbers(c: Ctx, t: number): void {
  const tm = now();
  if (tm < 8 * 60 || tm >= 19 * 60) return;
  const x0 = 1030, x1 = 1206;
  for (let i = 0; i < 4; i++) {
    const u = (t * 0.012 + i * 0.29) % 2;
    const up = u < 1;
    const k = up ? u : 2 - u;
    const x = x0 + (x1 - x0) * k;
    const gy = [436, 462, 500, 526][i];
    person(c, CLIMBERS[i], x, gy - ((x - 1024) / 192) * HC.plat, up ? 'right' : 'left', t + i, true, t * 0.5 + i * 0.3);
  }
}

/** A little hexagonal garden pavilion (亭) with a dark tiled roof. */
function pavilion(c: Ctx, x: number, y: number): void {
  shadow(c, x, y, 26, 5, 0.3, 30);
  block(c, x - 26, y - 5, 52, 5, '#9a968e', '#b8b4ac', '#7a766e');
  for (const px of [x - 22, x - 8, x + 6, x + 19]) block(c, px, y - 30, 3, 25, '#8a2a20', '#a8402e');
  block(c, x - 20, y - 14, 40, 2, '#7a5030', '#9a6a40');
  poly(c, '#2a3038', [x - 32, y - 28, x + 32, y - 28, x + 22, y - 40, x - 22, y - 40]);
  poly(c, '#3a424c', [x - 29, y - 29.4, x + 29, y - 29.4, x + 20, y - 39, x - 20, y - 39]);
  poly(c, '#4a525c', [x - 20, y - 39, x + 20, y - 39, x, y - 54]);
  for (let k = -3; k <= 3; k++) line(c, 'rgba(20,24,30,0.5)', 0.5, [x + k * 8, y - 30, x + k * 3, y - 50]);
  ellipse(c, '#d9b450', x, y - 55, 1.8, 2.2);
  poly(c, '#2a3038', [x - 32, y - 28, x - 36, y - 33, x - 28, y - 30]);
  poly(c, '#2a3038', [x + 32, y - 28, x + 36, y - 33, x + 28, y - 30]);
}

// ---------------------------------------------------------------------------
// Smaller CKS props
// ---------------------------------------------------------------------------

/** White perimeter wall with a blue tiled cap and flower-shaped lattice windows. */
function perimeterWall(c: Ctx, d: Decor): void {
  const w = d.w ?? 224, x0 = d.x - w / 2, by = d.y;
  block(c, x0, by - 16, w, 16, L.white, L.whiteLight, L.whiteShade, '#9a968e');
  rect(c, '#8a96a4', x0, by - 3, w, 3);
  for (let x = x0 + 16; x < x0 + w - 8; x += 32) {
    ellipse(c, '#a8b0b8', x, by - 9, 4.4, 4.4);
    ellipse(c, '#3f7a6a', x, by - 9, 3.4, 3.4);
    rect(c, L.white, x - 3.4, by - 9.3, 6.8, 0.6);
    rect(c, L.white, x - 0.3, by - 12.4, 0.6, 6.8);
    ellipse(c, L.white, x, by - 9, 1, 1);
  }
  // Tile cap.
  block(c, x0 - 1, by - 21, w + 2, 6, L.blueDark, L.blueLight);
  for (let x = x0 + 1; x < x0 + w; x += 3) rect(c, 'rgba(10,30,80,0.5)', x, by - 19.6, 0.5, 4);
  rect(c, L.blueDark, x0 - 1, by - 22.4, w + 2, 2);
}

/** A section of covered corridor: white posts, a blue-tiled roof, hanging lanterns. */
function corridor(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 160, x0 = d.x - w / 2, by = d.y;
  const depth = 32, H = 26;
  // Posts along the front edge (the back posts are hidden by the roof).
  for (let x = x0 + 2; x <= x0 + w; x += 32) {
    shadow(c, x + 1.5, by, 3, 1, 0.25);
    block(c, x, by - H, 3.4, H, '#f4f2ec', '#ffffff', '#cfc8bb');
    rect(c, '#b8322a', x, by - 4, 3.4, 4);
  }
  // Beam with brackets.
  block(c, x0, by - H - 4, w, 5, L.bracket, '#4aa898', '#1f6a5a');
  for (let x = x0 + 4; x < x0 + w; x += 8) rect(c, L.gold, x, by - H - 2.6, 1.4, 1.4);
  // Roof: front slope, then the top surface reaching back over the floor.
  poly(c, L.blueDark, [x0 - 3, by - H - 3, x0 + w + 3, by - H - 3, x0 + w, by - H - 12, x0, by - H - 12]);
  poly(c, L.blue, [x0, by - H - 12, x0 + w, by - H - 12, x0 + w, by - H - depth - 4, x0, by - H - depth - 4]);
  for (let x = x0 + 1; x < x0 + w; x += 3) {
    line(c, 'rgba(10,30,80,0.5)', 0.5, [x, by - H - 4, x, by - H - 12]);
    line(c, 'rgba(10,30,80,0.35)', 0.5, [x, by - H - 13, x, by - H - depth - 3]);
  }
  rect(c, L.blueLight, x0, by - H - 13, w, 1);
  rect(c, L.blueDark, x0, by - H - depth - 6, w, 3);
  // Red lanterns under the eaves.
  for (let x = x0 + 18; x < x0 + w; x += 32) {
    const sw = Math.sin(t * 1.4 + x) * 0.8 + wind.at(x, by) * 0.8;
    line(c, '#3a2a20', 0.5, [x, by - H, x + sw, by - H + 5]);
    ellipse(c, '#d6302a', x + sw, by - H + 8, 2.8, 3.4);
    rect(c, L.gold, x + sw - 1.4, by - H + 4.6, 2.8, 0.8);
    rect(c, L.gold, x + sw - 1.4, by - H + 11, 2.8, 0.8);
    if (isNight()) ellipse(c, 'rgba(255,140,90,0.3)', x + sw, by - H + 8, 7, 7);
  }
}

const SIGNS: [string, string][] = [
  ['自由廣場 →', 'LIBERTY SQUARE'],
  ['光華池', 'GUANGHUA POND'],
  ['雲漢池', 'YUNHAN POND'],
];

/** Palace-style lamp post: white pole, lantern head with a blue cap. */
function palaceLamp(c: Ctx, x: number, y: number): void {
  shadow(c, x, y, 4, 1.5);
  block(c, x - 3, y - 5, 6, 5, '#d6d2c8', '#f4f2ec');
  rect(c, '#f4f2ec', x - 1, y - 30, 2.4, 26);
  rect(c, 'rgba(0,0,0,0.12)', x + 0.6, y - 30, 0.8, 26);
  const night = isNight();
  block(c, x - 4, y - 40, 8.4, 10, night ? '#fff0b0' : '#f6f0dc', '#ffffff', '#c8b890');
  for (let k = 1; k < 3; k++) rect(c, '#b8322a', x - 4 + k * 2.8, y - 39, 0.5, 8);
  poly(c, L.blueDark, [x - 6, y - 40, x + 6.4, y - 40, x + 3, y - 44, x - 2.6, y - 44]);
  ellipse(c, L.gold, x + 0.2, y - 45, 1.2, 1.2);
  if (night) ellipse(c, 'rgba(255,230,150,0.3)', x, y - 35, 9, 9);
}

const KOI = ['#ff7a2a', '#f4f2ec', '#e8c048', '#d6302a', '#2b2e33', '#ffb070'];

function koiSchool(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 120;
  const n = 6;
  const v = d.variant ?? 0;
  for (let i = 0; i < n; i++) {
    const sp = 0.18 + (i % 3) * 0.05;
    const ph = t * sp + i * 1.7 + v;
    const x = d.x + Math.cos(ph) * (w * 0.4) * (0.6 + (i % 2) * 0.4);
    const y = d.y + Math.sin(ph * 1.3) * 18;
    const vx = -Math.sin(ph) * w * 0.4, vy = Math.cos(ph * 1.3) * 18 * 1.3;
    const a = Math.atan2(vy, vx);
    c.save();
    c.translate(x, y);
    c.rotate(a);
    ellipse(c, 'rgba(10,30,30,0.25)', 1, 1.6, 4.6, 1.8);
    const col = KOI[(i + v) % KOI.length];
    ellipse(c, col, 0, 0, 4.4, 1.7);
    if (col === '#f4f2ec') ellipse(c, '#ff6a2a', 1, -0.3, 1.8, 1);
    if (col === '#ff7a2a') ellipse(c, '#f4f2ec', -1.4, 0.2, 1.4, 0.9);
    const wag = Math.sin(t * 6 + i) * 1.4;
    poly(c, col, [-3.6, 0, -6.6, -1.8 + wag, -6.6, 1.8 + wag]);
    rect(c, '#1b1d24', 2.6, -0.8, 0.6, 0.6);
    c.restore();
  }
  // Ripples.
  const rk = (t * 0.4 + v * 0.3) % 1;
  c.strokeStyle = `rgba(255,255,255,${0.35 * (1 - rk)})`;
  c.lineWidth = 0.6;
  c.beginPath();
  c.ellipse(d.x + w * 0.2, d.y - 6, 2 + rk * 8, 1 + rk * 3, 0, 0, Math.PI * 2);
  c.stroke();
}

function lotus(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  const leaf = (lx: number, ly: number, r: number) => {
    ellipse(c, '#2f6a2a', lx, ly + 0.6, r, r * 0.55);
    ellipse(c, '#4f9a3a', lx, ly, r * 0.92, r * 0.5);
    line(c, '#2f6a2a', 0.4, [lx, ly, lx + r * 0.8, ly - r * 0.2]);
  };
  leaf(x - 6, y - 2, 4.4);
  leaf(x + 4, y + 1, 5);
  leaf(x + 1, y - 6, 3.6);
  // Flower on a stalk.
  line(c, '#3f7a35', 0.7, [x - 1, y - 2, x - 1, y - 10]);
  for (const [dx, col] of [[-2.4, '#f08ab0'], [2.4, '#f08ab0'], [0, '#ffb6d0']] as const) poly(c, col, [x - 1 + dx * 0.4, y - 10, x - 1 + dx, y - 13.6, x - 1 + dx * 0.2, y - 15.6]);
  ellipse(c, '#ffd34e', x - 1, y - 11, 0.9, 0.6);
  if ((d.variant ?? 0) === 1) {
    line(c, '#3f7a35', 0.6, [x + 6, y + 1, x + 6, y - 6]);
    ellipse(c, '#f8a8c8', x + 6, y - 7, 1.8, 2.2);
  }
}

function pigeonFig(c: Ctx, x: number, y: number, t: number, i: number, flying: boolean): void {
  if (flying) {
    const f = Math.sin(t * 18 + i) * 2.4;
    poly(c, '#7a8088', [x - 1, y, x - 5, y - f, x - 2, y + 0.6]);
    poly(c, '#7a8088', [x + 1, y, x + 5, y - f, x + 2, y + 0.6]);
    ellipse(c, '#9aa0a8', x, y, 2, 1.1);
    ellipse(c, '#5a6068', x + 1.6, y - 0.4, 0.9, 0.8);
    return;
  }
  const peck = Math.max(0, Math.sin(t * 4 + i * 1.9)) ** 4;
  ellipse(c, 'rgba(20,10,6,0.22)', x, y + 0.4, 2.4, 0.7);
  ellipse(c, '#9aa0a8', x, y - 2, 2.6, 1.6);
  ellipse(c, '#7a8088', x - 1.6, y - 2.4, 1.4, 1);
  const hx = x + 2 + peck * 0.6, hy = y - 3.6 + peck * 2.2;
  ellipse(c, '#5f8a6a', hx - 0.6, hy + 0.8, 1, 0.8);
  ellipse(c, '#6a7078', hx, hy, 1.1, 1);
  rect(c, '#e8a0a0', hx + 0.9, hy + 0.1, 0.8, 0.4);
  rect(c, '#d06060', x - 0.6, y - 0.6, 0.4, 0.8);
  rect(c, '#d06060', x + 0.6, y - 0.6, 0.4, 0.8);
}

function pigeonFlock(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 60, v = d.variant ?? 0;
  const cyc = (t + v * 7) % 26;
  const flying = cyc > 20;
  const u = flying ? (cyc - 20) / 6 : 0;
  for (let i = 0; i < 10; i++) {
    const bx = d.x + (((i * 37 + v * 11) % 100) / 100 - 0.5) * w + Math.sin(t * 0.2 + i) * 3;
    const by = d.y + (((i * 53 + v * 7) % 100) / 100 - 0.5) * 18;
    if (flying) {
      const a = u * Math.PI * 2 + i * 0.6;
      const alt = Math.sin(u * Math.PI) * 46;
      pigeonFig(c, bx + Math.cos(a) * 20 * Math.sin(u * Math.PI), by - alt + Math.sin(a) * 6, t, i, true);
    } else pigeonFig(c, bx, by, t, i, false);
  }
}

const KITERS: CharLook[] = [
  { skin: '#f3c9a0', hair: '#3a2418', hairStyle: 'cap', hat: '#e2493b', top: '#ffd34e', topShade: '#d9a82a', bottom: '#3a8fd9', shoes: '#e2493b', small: true },
  { skin: '#e8b48c', hair: '#2a1d16', hairStyle: 'neat', top: '#3a8fd9', topShade: '#2a6fb0', bottom: '#2f3442', shoes: '#1d1814' },
];

function kites(c: Ctx, d: Decor, t: number): void {
  const tm = now();
  if (tm < 9 * 60 || tm >= 18 * 60) return;
  const v = d.variant ?? 0;
  const fliers = v ? [[d.x - 20, d.y]] : [[d.x - 30, d.y], [d.x + 26, d.y - 6]];
  fliers.forEach(([fx, fy], i) => {
    person(c, KITERS[(i + v) % 2], fx, fy, 'up', t);
    const kx = fx + 14 + Math.sin(t * 0.5 + i * 2) * 10 + wind.at(fx, fy) * 6, ky = fy - 110 + Math.sin(t * 0.8 + i) * 8 - i * 14;
    // String (sagging curve).
    c.strokeStyle = 'rgba(255,255,255,0.7)';
    c.lineWidth = 0.4;
    c.beginPath();
    c.moveTo(fx + 3, fy - 16);
    c.quadraticCurveTo((fx + kx) / 2 + 10, (fy + ky) / 2 + 10, kx, ky);
    c.stroke();
    const kind = (i + v) % 3;
    if (kind === 0) {
      // Diamond kite with a bow tail.
      poly(c, '#e2493b', [kx, ky - 9, kx + 6, ky, kx, ky + 8, kx - 6, ky]);
      poly(c, '#ffd34e', [kx, ky - 9, kx + 6, ky, kx, ky]);
      poly(c, '#3a8fd9', [kx, ky + 8, kx - 6, ky, kx, ky]);
      line(c, '#5a3a20', 0.4, [kx, ky - 9, kx, ky + 8]);
      line(c, '#5a3a20', 0.4, [kx - 6, ky, kx + 6, ky]);
    } else {
      // A carp kite (鯉魚旗-style windsock).
      const sw = Math.sin(t * 3 + i) * 1.6;
      poly(c, '#3a8fd9', [kx - 6, ky - 3, kx + 6, ky - 2 + sw * 0.3, kx + 9, ky - 4 + sw, kx + 8, ky + sw * 0.6, kx + 9, ky + 3 + sw, kx + 6, ky + 2, kx - 6, ky + 3]);
      ellipse(c, '#ffffff', kx - 4, ky, 1.6, 1.6);
      ellipse(c, '#1b1d24', kx - 4, ky, 0.7, 0.7);
      for (let k = 0; k < 3; k++) rect(c, '#bfe6ff', kx - 1 + k * 2.6, ky - 1.6, 1, 3);
    }
    for (let k = 1; k <= 4; k++) {
      const tx2 = kx - Math.sin(t * 2 + k) * 2 - k * 1.2, ty2 = ky + 8 + k * 4;
      poly(c, k % 2 ? '#ffd34e' : '#ff7a7a', [tx2 - 1.6, ty2 - 1, tx2 + 1.6, ty2 + 1, tx2 + 1.6, ty2 - 1, tx2 - 1.6, ty2 + 1]);
    }
  });
}

const ELDERS: CharLook[] = [
  { skin: '#e8b48c', hair: '#e8e4dc', hairStyle: 'short', top: '#f4f1ea', topShade: '#d8d2c4', bottom: '#f4f1ea', shoes: '#1d1814' },
  { skin: '#f0c8a4', hair: '#c9c4bc', hairStyle: 'curly', top: '#c0392b', topShade: '#962a20', bottom: '#2a2f3d', shoes: '#1d1814' },
  { skin: '#e2b089', hair: '#8f8a86', hairStyle: 'bun', top: '#3f9a8a', topShade: '#2a7a6a', bottom: '#2a2f3d', shoes: '#1d1814', dress: true },
  { skin: '#d9a47a', hair: '#f4f1ea', hairStyle: 'neat', top: '#e8c048', topShade: '#c89a2a', bottom: '#f4f1ea', shoes: '#1d1814' },
  { skin: '#f0c8a4', hair: '#3a2418', hairStyle: 'bun', top: '#7d5aa8', topShade: '#5e4282', bottom: '#5e4282', shoes: '#1d1814', dress: true },
  { skin: '#e9bd96', hair: '#c9c4bc', hairStyle: 'short', top: '#2f8fa8', topShade: '#1f6a80', bottom: '#2a2f3d', shoes: '#1d1814' },
];

/** Morning tai chi: two rows of elders moving slowly in unison (6:00–10:00). */
function taichiGroup(c: Ctx, d: Decor, t: number): void {
  const tm = now();
  if (tm < 6 * 60 || tm >= 10 * 60) return;
  const sway = Math.sin(t * 0.7) * 1.6;
  for (let i = 0; i < 6; i++) {
    const col = i % 3, row = Math.floor(i / 3);
    const x = d.x - 36 + col * 36 + row * 16 + sway, y = d.y - 14 + row * 18;
    const f: Facing = Math.sin(t * 0.35) > 0.6 ? 'left' : Math.sin(t * 0.35) < -0.6 ? 'right' : 'down';
    person(c, ELDERS[i], x, y, f, t);
    // Arms pushing the air (hands drawn over the sprite).
    const ph = t * 0.7 + i * 0.05;
    const hx = Math.sin(ph) * 6, hy = -16 - Math.cos(ph) * 3;
    ellipse(c, ELDERS[i].skin, x + hx, y + hy, 1.3, 1.3);
    ellipse(c, ELDERS[i].skin, x - hx * 0.6, y + hy + 2, 1.3, 1.3);
  }
  // A little cassette player playing calm music.
  block(c, d.x + 56, d.y - 4, 8, 5, '#3a3e44', '#55606a');
  ellipse(c, '#9aa4ae', d.x + 58, d.y - 1.6, 1, 1);
  ellipse(c, '#9aa4ae', d.x + 62, d.y - 1.6, 1, 1);
  const u = (t * 0.3) % 1;
  c.globalAlpha = 1 - u;
  txt(c, '♪', d.x + 60, d.y - 8 - u * 12, 5, '#ffffff', 900, 'rgba(0,0,0,0.3)');
  c.globalAlpha = 1;
}

const DANCERS: CharLook[] = [
  { skin: '#f3cba6', hair: '#4a2a1a', hairStyle: 'bun', top: '#ff6f91', topShade: '#d94f72', bottom: '#2a2f3d', shoes: '#f4f1ea', dress: true },
  { skin: '#e9bd96', hair: '#2a1d16', hairStyle: 'pony', top: '#ffd34e', topShade: '#d9a82a', bottom: '#2a2f3d', shoes: '#f4f1ea' },
  { skin: '#f6d2b0', hair: '#141218', hairStyle: 'long', top: '#3a8fd9', topShade: '#2a6fb0', bottom: '#f4f1ea', shoes: '#f4f1ea' },
  { skin: '#e8b48c', hair: '#3a2418', hairStyle: 'curly', top: '#5fbf5a', topShade: '#3f9a3a', bottom: '#2a2f3d', shoes: '#1d1814', dress: true },
  { skin: '#f0c8a4', hair: '#141218', hairStyle: 'short', top: '#c58cff', topShade: '#a06ad8', bottom: '#2a2f3d', shoes: '#1d1814' },
  { skin: '#f3cba6', hair: '#4a2a1a', hairStyle: 'pigtails', top: '#ff9f43', topShade: '#d97a2a', bottom: '#3a5a8a', shoes: '#f4f1ea' },
];

/** Evening dance groups practising with a speaker (18:00–22:00). Variant 1: a hip-hop crew. */
function danceGroup(c: Ctx, d: Decor, t: number): void {
  const tm = now();
  if (tm < 18 * 60 || tm >= 22 * 60) return;
  const v = d.variant ?? 0;
  const beat = t * (v ? 3.2 : 2.4);
  const turn: Facing[] = ['down', 'left', 'up', 'right'];
  for (let i = 0; i < (v ? 4 : 6); i++) {
    const col = i % 3, row = Math.floor(i / 3);
    const step = Math.sin(beat * 0.5) * 6;
    const x = d.x - 30 + col * 28 + step * (row ? -1 : 1), y = d.y - 8 + row * 16;
    const f = turn[Math.floor(beat / 4 + (v ? i : 0)) % 4];
    person(c, DANCERS[(i + v * 2) % DANCERS.length], x, y, f, t, true, beat * 0.25, Math.max(0, Math.sin(beat * Math.PI * 0.5)) * (v ? 3 : 1.4));
  }
  // The speaker on a little trolley, pulsing.
  const sx = d.x + 54, sy = d.y + 4;
  const pulse = Math.max(0, Math.sin(beat * Math.PI)) * 0.6;
  block(c, sx - 5, sy - 13, 10, 13, '#2b2e33', '#4a4e54');
  ellipse(c, '#6a6e74', sx, sy - 9, 2.6 + pulse, 2.6 + pulse);
  ellipse(c, '#1b1d22', sx, sy - 9, 1.2, 1.2);
  ellipse(c, '#6a6e74', sx, sy - 3.4, 1.6, 1.6);
  if (isNight()) ellipse(c, 'rgba(120,200,255,0.25)', sx + 1, sy - 12, 1.4, 1.4);
  for (let k = 0; k < 2; k++) {
    const u = (t * 0.45 + k / 2) % 1;
    c.globalAlpha = 1 - u;
    txt(c, k ? '♫' : '♪', sx - 4 + k * 8, sy - 18 - u * 16, 6, '#ffd34e', 900, 'rgba(0,0,0,0.35)');
    c.globalAlpha = 1;
  }
}

const KID: CharLook = { skin: '#f8d6b8', hair: '#1d1a22', hairStyle: 'cap', hat: '#ffd34e', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#2f4a8a', shoes: '#f4f1ea', small: true };

/** A school group in yellow caps following their teacher's flag (9:00–15:00). */
function schoolGroup(c: Ctx, d: Decor, t: number): void {
  const tm = now();
  if (tm < 9 * 60 || tm >= 15 * 60) return;
  const lead = Math.sin(t * 0.08 + (d.variant ?? 0) * 2) * 30;
  const moving = Math.abs(Math.cos(t * 0.08)) > 0.3;
  const f: Facing = Math.cos(t * 0.08) > 0 ? 'right' : 'left';
  for (let i = 0; i < 8; i++) {
    const col = i % 4, row = Math.floor(i / 4);
    const x = d.x + lead - 30 + col * 12 + (row ? 6 : 0), y = d.y - 4 + row * 10;
    const v = d.variant ?? 0;
    person(c, { ...KID, hat: v ? '#e2493b' : '#ffd34e', top: v ? '#3a8fd9' : '#f4f1ea', hair: i % 2 ? '#3a2418' : '#1d1a22', bottom: i % 3 ? '#2f4a8a' : '#5a6f8a', hairStyle: 'cap' }, x, y, f, t + i, moving, t * 0.9 + i * 0.3);
  }
  // The teacher's little flag on a pole.
  const fx = d.x + lead + (f === 'right' ? 26 : -40), fy = d.y - 6;
  rect(c, '#55606a', fx, fy - 30, 0.8, 22);
  const sw = Math.sin(t * 3) * 1;
  poly(c, '#ffd34e', [fx + 0.8, fy - 30, fx + 9, fy - 27 + sw, fx + 0.8, fy - 24]);
}

const TOURISTS: CharLook[] = [
  { skin: '#f8dcc4', hair: '#e8c25e', hairStyle: 'pony', top: '#3fa36f', topShade: '#2a7a50', bottom: '#5a6f8a', shoes: '#f4f1ea', hat: '#f4f1ea' },
  { skin: '#c98a5e', hair: '#141218', hairStyle: 'short', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#6b7a5a', shoes: '#5a3a20' },
  { skin: '#f3cba6', hair: '#2a1d16', hairStyle: 'long', top: '#ff7a7a', topShade: '#d95a5a', bottom: '#2a2f3d', shoes: '#f4f1ea' },
];

/** A couple of tourists taking a selfie / pointing their camera at the landmark (daytime). */
function touristGroup(c: Ctx, d: Decor, t: number): void {
  const tm = now();
  if (tm < 8 * 60 || tm >= 19 * 60) return;
  const v = d.variant ?? 0;
  for (let i = 0; i < 3; i++) {
    const x = d.x - 18 + i * 16, y = d.y + (i % 2) * 4;
    const look = TOURISTS[(i + v) % 3];
    person(c, look, x, y, v ? 'right' : 'up', t + i);
    if (i === 1) {
      // A phone held up for a photo, flashing now and then.
      block(c, x + 3, y - 24, 3.6, 5, '#2b2e33', '#4a4e54');
      if (Math.sin(t * 0.9 + v) > 0.985) ellipse(c, 'rgba(255,255,255,0.8)', x + 4.8, y - 22, 5, 5);
    }
  }
}

const SELLER: CharLook = { skin: '#e2b089', hair: '#2a1d16', hairStyle: 'cap', hat: '#c0392b', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#3a4a6a', shoes: '#1d1814' };

/** A balloon seller with a bunch of bobbing balloons (daytime). */
function balloonSeller(c: Ctx, d: Decor, t: number): void {
  const tm = now();
  if (tm < 9 * 60 || tm >= 20 * 60) return;
  person(c, SELLER, d.x, d.y, 'down', t);
  const hx = d.x + 6, hy = d.y - 16;
  const cols = ['#e2493b', '#ffd34e', '#3a8fd9', '#5fbf5a', '#ff8ac0', '#c58cff', '#ff9f43'];
  cols.forEach((col, i) => {
    const a = -Math.PI / 2 + (i - 3) * 0.28;
    const len = 26 + (i % 3) * 5;
    const bx = hx + Math.cos(a) * len * 0.5 + Math.sin(t * 1.1 + i) * 1.4 + wind.at(hx, hy) * 2, by = hy + Math.sin(a) * len;
    line(c, 'rgba(255,255,255,0.7)', 0.35, [hx, hy, bx, by + 4]);
    ellipse(c, col, bx, by, 3.6, 4.4);
    ellipse(c, 'rgba(255,255,255,0.5)', bx - 1.2, by - 1.6, 0.9, 1.3);
    poly(c, col, [bx - 0.8, by + 4.6, bx + 0.8, by + 4.6, bx, by + 3.6]);
  });
}

// ---------------------------------------------------------------------------
// Beitou buildings
// ---------------------------------------------------------------------------

/** A Japanese-style tiled roof (dark grey) seen from above the front: hip-and-gable. */
function japRoof(c: Ctx, x0: number, w: number, yb: number, h: number, col = '#4a525c', gable = true): void {
  const light = shade(col, 0.25), dark = shade(col, -0.35);
  poly(c, dark, [x0 - 6, yb, x0 + w + 6, yb, x0 + w - 6, yb - h, x0 + 6, yb - h]);
  poly(c, col, [x0 - 4, yb - 1.4, x0 + w + 4, yb - 1.4, x0 + w - 7, yb - h + 1, x0 + 7, yb - h + 1]);
  for (let x = x0 - 2; x < x0 + w + 2; x += 2.6) {
    const k = (x - (x0 + w / 2)) / (w / 2);
    line(c, 'rgba(20,24,30,0.45)', 0.5, [x, yb - 1.6, x - k * 9, yb - h + 1.6]);
  }
  rect(c, light, x0 - 3, yb - 2, w + 6, 0.8);
  // Ridge with flared ends.
  block(c, x0 + 4, yb - h - 3, w - 8, 4, dark, light);
  poly(c, dark, [x0 + 4, yb - h - 3, x0, yb - h - 6, x0 + 6, yb - h + 1]);
  poly(c, dark, [x0 + w - 4, yb - h - 3, x0 + w, yb - h - 6, x0 + w - 6, yb - h + 1]);
  if (gable) {
    // A small gable triangle (with a white fish-scale board) in the middle of the front.
    const gx = x0 + w / 2;
    poly(c, dark, [gx - 14, yb - 1, gx + 14, yb - 1, gx, yb - h * 0.75]);
    poly(c, '#e8dcc0', [gx - 10, yb - 2.6, gx + 10, yb - 2.6, gx, yb - h * 0.64]);
    rect(c, '#8a6440', gx - 0.4, yb - h * 0.6, 0.8, h * 0.55);
  }
}

/** Wooden board wall with a timber frame. */
function boards(c: Ctx, x: number, y: number, w: number, h: number, col: string): void {
  block(c, x, y, w, h, col, shade(col, 0.12), shade(col, -0.2));
  for (let yy = y + 3; yy < y + h - 1; yy += 3) rect(c, 'rgba(60,30,10,0.18)', x + 1, yy, w - 2, 0.5);
  for (let xx = x + 12; xx < x + w - 2; xx += 24) rect(c, '#5a3a20', xx, y, 2, h);
  rect(c, '#5a3a20', x, y, w, 2);
  rect(c, '#5a3a20', x, y + h - 2, w, 2);
}

function windowGrid(c: Ctx, x: number, y: number, w: number, h: number, night: boolean, frame = '#f4f1ea'): void {
  block(c, x, y, w, h, frame, '#ffffff');
  vgrad(c, x + 1, y + 1, w - 2, h - 2, night ? '#ffe8a8' : '#5a7a8a', night ? '#e8a858' : '#2a4a5a');
  rect(c, frame, x + w / 2 - 0.4, y, 0.8, h);
  rect(c, frame, x, y + h / 2 - 0.4, w, 0.8);
  if (!night) rect(c, 'rgba(255,255,255,0.35)', x + 1.6, y + 1.4, 1, h - 3);
}

function mrtStationArt(c: Ctx, cx: number, by: number, night: boolean): void {
  // Xinbeitou MRT: a white steel-and-glass hall with a long shallow arched roof.
  const W = 224, x0 = cx - W / 2;
  shadow(c, cx, by, W / 2 + 4, 4, 0.3, 40);
  block(c, x0, by - 6, W, 6, '#c8ccd0', '#e0e4e8', '#9aa0a8');
  // Glass curtain wall.
  vgrad(c, x0 + 6, by - 52, W - 12, 46, night ? '#ffe8b0' : '#8ac0c8', night ? '#d89858' : '#3a7080');
  for (let x = x0 + 6; x < x0 + W - 6; x += 16) block(c, x, by - 52, 2.4, 46, '#f4f5f2', '#ffffff');
  rect(c, '#f4f5f2', x0 + 6, by - 30, W - 12, 1.4);
  if (!night) for (let x = x0 + 12; x < x0 + W - 12; x += 32) poly(c, 'rgba(255,255,255,0.3)', [x, by - 50, x + 6, by - 50, x - 4, by - 8, x - 10, by - 8]);
  // Arched roof (shallow barrel vault) with a white rim.
  c.fillStyle = '#c8ccd0';
  c.beginPath();
  c.moveTo(x0 - 6, by - 52);
  c.quadraticCurveTo(cx, by - 92, x0 + W + 6, by - 52);
  c.lineTo(x0 + W + 6, by - 48);
  c.quadraticCurveTo(cx, by - 86, x0 - 6, by - 48);
  c.fill();
  c.fillStyle = '#eef0f2';
  c.beginPath();
  c.moveTo(x0 - 4, by - 53);
  c.quadraticCurveTo(cx, by - 94, x0 + W + 4, by - 53);
  c.quadraticCurveTo(cx, by - 104, x0 - 4, by - 53);
  c.fill();
  for (let i = 1; i < 10; i++) {
    const u = i / 10, xx = x0 - 4 + (W + 8) * u;
    const yy = by - 53 - Math.sin(u * Math.PI) * 44;
    line(c, 'rgba(120,130,140,0.4)', 0.5, [xx, yy + 2, xx, yy - 6]);
  }
  // Name sign: 新北投 with the red line stripe.
  block(c, cx - 40, by - 68, 80, 13, '#ffffff', '#ffffff', '#c8ccd0');
  rect(c, '#e2493b', cx - 39, by - 57.4, 78, 1.4);
  ellipse(c, '#1f6fc0', cx - 31, by - 62, 4, 4);
  ellipse(c, '#ffffff', cx - 31, by - 62, 2.4, 2.4);
  ellipse(c, '#1f6fc0', cx - 31, by - 62, 1.2, 1.2);
  txt(c, '新北投站', cx + 4, by - 63.6, 6, '#1f3a5e', 900);
  txt(c, 'XINBEITOU STATION', cx + 4, by - 59, 2.3, '#55606a', 700);
}

function oldStationArt(c: Ctx, cx: number, by: number, night: boolean): void {
  // The restored 1916 Xinbeitou station: cream boards, brown timber, a dark tiled roof with a dormer.
  const W = 180, x0 = cx - W / 2;
  shadow(c, cx, by, W / 2 + 4, 4, 0.3, 30);
  block(c, x0 - 4, by - 5, W + 8, 5, '#9a968e', '#b8b4ac', '#7a766e');
  boards(c, x0, by - 40, W, 35, '#e8dcbc');
  // Windows and the double door.
  for (let i = 0; i < 6; i++) {
    const wx = x0 + 10 + i * 28 + (i >= 3 ? 8 : 0);
    if (i === 3) continue;
    windowGrid(c, wx, by - 34, 14, 16, night, '#f4ecd8');
  }
  block(c, cx - 12, by - 36, 24, 31, '#6a4428', '#8a5a34', '#4a2e18');
  windowGrid(c, cx - 10, by - 34, 9, 18, night, '#d8c8a0');
  windowGrid(c, cx + 1, by - 34, 9, 18, night, '#d8c8a0');
  // Covered entrance porch with its own little gable.
  for (const px of [cx - 22, cx + 19]) block(c, px, by - 46, 3, 41, '#6a4428', '#8a5a34');
  japRoof(c, cx - 26, 52, by - 44, 14, '#3a424c', true);
  // Main roof with the tall dormer window (老虎窗).
  japRoof(c, x0 - 2, W + 4, by - 40, 34, '#4a525c', false);
  block(c, cx - 10, by - 72, 20, 16, '#e8dcbc', '#f4ecd8', '#c8b890');
  windowGrid(c, cx - 7, by - 70, 14, 11, night, '#f4ecd8');
  poly(c, '#3a424c', [cx - 14, by - 72, cx + 14, by - 72, cx, by - 84]);
  // Name board 新北投驛 (the Japanese-era name).
  block(c, cx - 22, by - 54, 44, 9, '#f4ecd8', '#ffffff', '#c8b890');
  txt(c, '新北投驛', cx, by - 49.4, 5.6, '#2a1a10', 900);
}

function libraryArt(c: Ctx, cx: number, by: number, night: boolean): void {
  // Beitou Public Library: a long green-roofed wooden building on stilts, with big slatted
  // balconies all round and solar panels on the sloping eco-roof.
  const W = 228, x0 = cx - W / 2;
  shadow(c, cx, by, W / 2 + 6, 5, 0.32, 40);
  // Stone footing and stilts.
  block(c, x0 + 4, by - 6, W - 8, 6, '#8a8a84', '#a8a8a0', '#6a6a64');
  // Ground floor: glass and wood.
  vgrad(c, x0 + 10, by - 34, W - 20, 28, night ? '#ffe8b0' : '#6a8a8a', night ? '#e0a860' : '#3a5a5a');
  for (let x = x0 + 10; x < x0 + W - 10; x += 14) block(c, x, by - 34, 2.4, 28, '#8a5a34', '#a8784a');
  // Bookshelves glimpsed inside.
  if (!night) for (let x = x0 + 16; x < x0 + W - 16; x += 14) for (let k = 0; k < 3; k++) rect(c, ['#c0392b', '#3a8fd9', '#e8c048'][(x + k) % 3], x + k * 3, by - 18, 2, 6);
  // Upper balcony: deck, slatted wooden louvres.
  block(c, x0, by - 40, W, 6, '#7a5030', '#9a6a40', '#5a3a20');
  vgrad(c, x0 + 6, by - 70, W - 12, 30, night ? '#ffe0a0' : '#5a7a7a', night ? '#d89850' : '#3a5050');
  for (let x = x0 + 2; x < x0 + W - 1; x += 3.2) rect(c, '#a8784a', x, by - 72, 1.8, 32);
  rect(c, '#7a5030', x0, by - 72, W, 2);
  rect(c, '#7a5030', x0, by - 56, W, 1.4);
  // The big sloping eco-roof: green grass on top with a band of solar panels.
  poly(c, '#5a3a20', [x0 - 10, by - 72, x0 + W + 10, by - 72, x0 + W + 4, by - 78, x0 - 4, by - 78]);
  poly(c, '#5f9a4a', [x0 - 4, by - 78, x0 + W + 4, by - 78, x0 + W - 6, by - 118, x0 + 6, by - 118]);
  for (let i = 0; i < 40; i++) {
    const gx = x0 + 4 + ((i * 53) % (W - 8)), gy = by - 82 - ((i * 29) % 34);
    rect(c, i % 3 ? '#4f8a3a' : '#7ab05a', gx, gy, 1.4, 1);
  }
  // Solar panels in rows.
  for (let row = 0; row < 2; row++) {
    for (let i = 0; i < 9; i++) {
      const px = x0 + 22 + i * 21 + row * 4, py = by - 108 + row * 12;
      block(c, px, py, 17, 8, '#1f3a6e', '#3a5a9a', '#15284e');
      rect(c, 'rgba(160,200,255,0.4)', px + 1, py + 3.6, 15, 0.4);
      for (let k = 1; k < 4; k++) rect(c, 'rgba(160,200,255,0.3)', px + k * 4.2, py + 1, 0.4, 6);
    }
  }
  rect(c, '#7a5030', x0 + 6, by - 120, W - 12, 2.4);
  // Wooden name sign by the entrance.
  block(c, cx - 26, by - 30, 52, 9, '#5a3a20', '#7a5030');
  txt(c, '北投圖書館', cx, by - 25.4, 5.4, '#f4ecd8', 900);
}

function museumArt(c: Ctx, cx: number, by: number, night: boolean): void {
  // Beitou Hot Spring Museum (1913): red-brick ground floor with arches, a wooden upper floor
  // with a veranda, and grey tiled roofs with gables.
  const W = 176, x0 = cx - W / 2;
  shadow(c, cx, by, W / 2 + 6, 5, 0.32, 40);
  block(c, x0 - 4, by - 5, W + 8, 5, '#9a968e', '#b8b4ac');
  // Brick ground floor.
  block(c, x0, by - 38, W, 33, '#a8483a', '#c05a48', '#7a3028');
  for (let y = by - 36; y < by - 6; y += 2.5) for (let x = x0 + ((y / 2.5) % 2 ? 0 : 3); x < x0 + W; x += 6) rect(c, 'rgba(255,230,210,0.25)', x, y, 0.5, 2.5);
  for (let y = by - 36; y < by - 6; y += 2.5) rect(c, 'rgba(255,230,210,0.3)', x0 + 1, y, W - 2, 0.5);
  // Arched windows with white surrounds (stained glass inside).
  for (let i = 0; i < 6; i++) {
    const wx = x0 + 12 + i * 28;
    c.fillStyle = '#f4ecd8';
    c.beginPath();
    c.moveTo(wx - 1.4, by - 8);
    c.lineTo(wx - 1.4, by - 26);
    c.arc(wx + 7, by - 26, 8.4, Math.PI, 0);
    c.lineTo(wx + 15.4, by - 8);
    c.fill();
    c.fillStyle = night ? '#ffd890' : '#3a5a6a';
    c.beginPath();
    c.moveTo(wx, by - 9);
    c.lineTo(wx, by - 26);
    c.arc(wx + 7, by - 26, 7, Math.PI, 0);
    c.lineTo(wx + 14, by - 9);
    c.fill();
    if (!night) for (let k = 0; k < 3; k++) rect(c, ['#e8c048', '#3fa36f', '#c0392b'][k], wx + 2 + k * 4, by - 30, 2, 2);
    rect(c, '#f4ecd8', wx + 6.6, by - 32, 0.8, 23);
  }
  // Wooden upper floor with veranda windows.
  boards(c, x0 + 6, by - 66, W - 12, 28, '#e8dcbc');
  for (let i = 0; i < 9; i++) windowGrid(c, x0 + 12 + i * 18, by - 62, 12, 14, night, '#f4ecd8');
  rect(c, '#5a3a20', x0 + 4, by - 44, W - 8, 2);
  for (let x = x0 + 6; x < x0 + W - 4; x += 4) rect(c, '#7a5030', x, by - 44, 1, -5);
  // Roofs: a long hip roof with two front gables.
  japRoof(c, x0 - 2, W + 4, by - 66, 34, '#4a525c', false);
  for (const gx of [x0 + 34, x0 + W - 34]) {
    poly(c, '#3a424c', [gx - 22, by - 66, gx + 22, by - 66, gx, by - 94]);
    poly(c, '#e8dcbc', [gx - 17, by - 67, gx + 17, by - 67, gx, by - 89]);
    for (let k = -2; k <= 2; k++) rect(c, '#7a5030', gx + k * 5 - 0.4, by - 80 + Math.abs(k) * 3, 0.8, 13 - Math.abs(k) * 3);
    windowGrid(c, gx - 4, by - 80, 8, 8, night, '#f4ecd8');
  }
  // Name plate.
  block(c, cx - 30, by - 44, 60, 8, '#f4ecd8', '#ffffff', '#c8b890');
  txt(c, '北投溫泉博物館', cx, by - 39.8, 4.8, '#4a2410', 900);
}

function plumArt(c: Ctx, cx: number, by: number, night: boolean): void {
  // 梅庭: a small Japanese-style house behind a low stone wall, with a plum tree in blossom.
  const W = 110, x0 = cx - W / 2;
  shadow(c, cx, by, W / 2 + 4, 4, 0.3, 30);
  boards(c, x0 + 6, by - 34, W - 12, 26, '#d8c8a8');
  for (let i = 0; i < 4; i++) windowGrid(c, x0 + 14 + i * 22, by - 30, 14, 14, night, '#f4ecd8');
  japRoof(c, x0 + 2, W - 4, by - 34, 26, '#3a424c', false);
  // Low stone wall with the name plaque.
  block(c, x0 - 4, by - 9, W + 8, 9, '#8a8a84', '#a8a8a0', '#6a6a64');
  for (let x = x0; x < x0 + W + 4; x += 7) rect(c, 'rgba(0,0,0,0.15)', x, by - 8, 0.5, 8);
  block(c, cx - 12, by - 16, 24, 10, '#2a1a10', '#4a2e1a');
  txt(c, '梅庭', cx, by - 11, 6, '#f4ecd8', 900);
  // Plum tree: dark crooked branches with pink and white blossom.
  const tx = x0 + W - 6;
  line(c, '#3a2418', 2.4, [tx, by, tx - 4, by - 20, tx + 6, by - 34]);
  line(c, '#3a2418', 1.4, [tx - 4, by - 20, tx - 14, by - 30]);
  line(c, '#3a2418', 1, [tx + 2, by - 28, tx + 14, by - 32]);
  for (let i = 0; i < 26; i++) {
    const bx = tx - 16 + ((i * 37) % 32), byy = by - 24 - ((i * 23) % 16);
    ellipse(c, i % 3 ? '#f8b8d0' : '#ffffff', bx, byy, 1.6, 1.4);
    if (i % 4 === 0) ellipse(c, '#e86890', bx, byy, 0.5, 0.5);
  }
}

const HOTEL_NAMES: [string, string, string][] = [
  ['雲泉旅館', 'CLOUD SPRING INN', '#c0392b'],
  ['山月湯屋', 'MOUNTAIN MOON BATHS', '#2f6a5a'],
  ['松風溫泉', 'PINE WIND HOT SPRING', '#7a3a8a'],
];

function hotelArt(c: Ctx, cx: number, by: number, v: number, night: boolean): void {
  const W = 108, x0 = cx - W / 2;
  const [zh, en, col] = HOTEL_NAMES[v % 3];
  shadow(c, cx, by, W / 2 + 4, 4, 0.3, 40);
  block(c, x0 - 2, by - 5, W + 4, 5, '#8a8a84', '#a8a8a0');
  // Two storeys: white plaster with dark timber, and a wooden balcony upstairs.
  block(c, x0, by - 30, W, 25, '#f2ece0', '#ffffff', '#d8d0c0');
  for (let i = 0; i < 5; i++) windowGrid(c, x0 + 6 + i * 20, by - 26, 12, 13, night, '#5a3a20');
  block(c, cx - 9, by - 22, 18, 17, '#5a3a20', '#7a5030');
  // Noren curtain with the hot-spring sign ♨.
  block(c, cx - 8, by - 22, 16, 9, col, shade(col, 0.2));
  rect(c, 'rgba(255,255,255,0.4)', cx - 2.8, by - 22, 0.5, 9);
  rect(c, 'rgba(255,255,255,0.4)', cx + 2.6, by - 22, 0.5, 9);
  txt(c, '♨', cx, by - 17.6, 6, '#ffffff', 900);
  japRoof(c, x0 - 2, W + 4, by - 30, 12, '#3a424c', false);
  boards(c, x0 + 6, by - 56, W - 12, 22, '#c8a878');
  for (let i = 0; i < 4; i++) windowGrid(c, x0 + 12 + i * 23, by - 52, 14, 12, night, '#5a3a20');
  for (let x = x0 + 6; x < x0 + W - 6; x += 3) rect(c, '#5a3a20', x, by - 40, 1, 6);
  rect(c, '#5a3a20', x0 + 6, by - 41, W - 12, 1.4);
  japRoof(c, x0 + 2, W - 4, by - 56, 26, '#4a525c', true);
  // Vertical name sign.
  block(c, x0 - 8, by - 62, 10, 40, col, shade(col, 0.2), shade(col, -0.3));
  [...zh].forEach((ch, i) => txt(c, ch, x0 - 3, by - 56 + i * 9, 6, '#fff6dc', 900, 'rgba(0,0,0,0.35)'));
  txt(c, en, cx, by - 33, 2.2, '#3a2a20', 700);
}

/** Red paper lanterns on a string (bath gate, old station), swaying. */
function lanternRow(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 160, x0 = d.x - w / 2;
  const top = d.y - 46;
  c.strokeStyle = '#3a2a20';
  c.lineWidth = 0.5;
  c.beginPath();
  c.moveTo(x0, top);
  c.quadraticCurveTo(d.x, top + 8, x0 + w, top);
  c.stroke();
  const n = Math.max(3, Math.round(w / 22));
  for (let i = 1; i < n; i++) {
    const u = i / n;
    const lx = x0 + w * u, ly = top + Math.sin(u * Math.PI) * 4;
    const sw = Math.sin(t * 1.3 + i) * 0.8 + wind.at(lx, ly) * 0.8;
    line(c, '#3a2a20', 0.4, [lx, ly, lx + sw, ly + 3]);
    ellipse(c, (d.variant ?? 0) ? '#f4ecd8' : '#d6302a', lx + sw, ly + 6.4, 2.8, 3.4);
    rect(c, '#2a1a10', lx + sw - 1.6, ly + 2.8, 3.2, 0.8);
    rect(c, '#2a1a10', lx + sw - 1.6, ly + 9.4, 3.2, 0.8);
    if (isNight()) ellipse(c, 'rgba(255,150,90,0.3)', lx + sw, ly + 6.4, 7, 7);
  }
}

const BATHERS: [number, number, number][] = [
  [42, 8, 0], [44, 9, 1], [47, 8, 2], [49, 9, 3], [52, 8, 4], [43, 12, 5], [46, 11, 1], [49, 12, 2], [51, 11, 0],
];
const BATHER_HAIR = ['#141218', '#3a2418', '#c9c4bc', '#2a1d16', '#141218', '#8f8a86'];

/** Heads and shoulders of bathers in swimsuits in the Millennium Hot Spring pools. */
function bathers(c: Ctx, _d: Decor, t: number): void {
  const tm = now();
  if (tm < 5 * 60 + 30 || tm >= 22 * 60) return;
  for (const [tx, ty, k] of BATHERS) {
    const x = tx * 16 + 8 + Math.sin(t * 0.3 + k) * 2, y = ty * 16 + 8 + Math.sin(t * 0.9 + k) * 0.5;
    // Ripple ring at the waterline.
    c.strokeStyle = 'rgba(255,255,255,0.45)';
    c.lineWidth = 0.6;
    c.beginPath();
    c.ellipse(x, y, 5 + Math.sin(t * 1.2 + k) * 0.6, 1.8, 0, 0, Math.PI * 2);
    c.stroke();
    // Shoulders just under the surface, then the head.
    ellipse(c, 'rgba(232,180,140,0.55)', x, y + 0.6, 4.4, 1.4);
    const skin = k % 3 === 0 ? '#e8b48c' : k % 3 === 1 ? '#f0c8a4' : '#d9a47a';
    ellipse(c, skin, x, y - 3, 2.8, 3);
    ellipse(c, BATHER_HAIR[k % BATHER_HAIR.length], x, y - 4.6, 2.9, 1.8);
    if (k % 3 === 2) block(c, x - 2.4, y - 7.4, 4.8, 2, '#ffffff', '#ffffff');
    rect(c, '#1b1d24', x - 1.2, y - 3, 0.6, 0.6);
    rect(c, '#1b1d24', x + 0.6, y - 3, 0.6, 0.6);
  }
}

function steamPuffs(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  if (v >= 3) {
    // Thermal Valley: thick drifting clouds of steam.
    for (let i = 0; i < 6; i++) {
      const k = (t * 0.1 + i / 6 + v * 0.13) % 1;
      const x = d.x + Math.sin(i * 2.1 + v) * 16 + k * 14 + wind.at(d.x, d.y) * 10 * k;
      const y = d.y - 4 - k * 56;
      const a = 0.55 * Math.sin(k * Math.PI);
      ellipse(c, `rgba(236,246,240,${a})`, x, y, 9 + k * 14, 5 + k * 8);
      ellipse(c, `rgba(255,255,255,${a * 0.6})`, x - 2, y - 2, 5 + k * 8, 3 + k * 5);
    }
    return;
  }
  for (let i = 0; i < 3; i++) {
    const k = (t * 0.22 + i / 3 + v * 0.27) % 1;
    const x = d.x + Math.sin(t * 0.7 + i * 2 + v) * 4 + wind.at(d.x, d.y) * 6 * k;
    const y = d.y - 4 - k * 30;
    const a = 0.5 * Math.sin(k * Math.PI);
    ellipse(c, `rgba(246,250,248,${a})`, x, y, 5 + k * 8, 3 + k * 5);
    ellipse(c, `rgba(255,255,255,${a * 0.5})`, x - 1, y - 1.4, 3 + k * 5, 2 + k * 3);
  }
}

function rock(c: Ctx, d: Decor): void {
  const v = d.variant ?? 0;
  const s = [1, 1.3, 0.8][v % 3];
  const x = d.x, y = d.y;
  shadow(c, x, y, 9 * s, 2.4);
  c.fillStyle = '#6a6a64';
  c.beginPath();
  c.moveTo(x - 9 * s, y);
  c.quadraticCurveTo(x - 10 * s, y - 8 * s, x - 2 * s, y - 10 * s);
  c.quadraticCurveTo(x + 7 * s, y - 11 * s, x + 9 * s, y - 2 * s);
  c.lineTo(x + 8 * s, y);
  c.fill();
  ellipse(c, '#8a8a84', x - 2 * s, y - 7 * s, 5 * s, 2.6 * s);
  ellipse(c, '#5f8a4a', x + 2 * s, y - 9 * s, 3 * s, 1.4 * s);
  ellipse(c, '#d8c460', x - 6 * s, y - 2 * s, 1.6 * s, 0.8 * s);
}

function dangerSign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 5, 1.4);
  rect(c, '#55606a', x - 0.6, y - 22, 1.4, 22);
  block(c, x - 11, y - 34, 22, 14, '#ffd34e', '#ffe680', '#c8a020');
  poly(c, '#1b1d24', [x - 8.6, y - 22.6, x - 4, y - 22.6, x - 6.3, y - 27]);
  poly(c, '#ffd34e', [x - 7.6, y - 23.2, x - 5, y - 23.2, x - 6.3, y - 25.6]);
  if ((d.variant ?? 0) === 0) {
    txt(c, '危險', x + 3, y - 30, 4.4, '#c0392b', 900);
    txt(c, 'DANGER 90°C', x + 3, y - 25.6, 2.4, '#1b1d24', 900);
  } else {
    txt(c, '小心燙', x + 3, y - 30, 4.2, '#c0392b', 900);
    txt(c, 'HOT WATER!', x + 3, y - 25.6, 2.4, '#1b1d24', 900);
  }
}

function btLamp(c: Ctx, x: number, y: number): void {
  // A Japanese garden lamp post: dark wooden post, square paper lantern with a little roof.
  shadow(c, x, y, 3.6, 1.4);
  rect(c, '#3a2a20', x - 1, y - 26, 2.4, 26);
  const night = isNight();
  block(c, x - 3.6, y - 34, 7.6, 8, night ? '#fff0c0' : '#f4ecd8', '#ffffff', '#c8b890');
  rect(c, '#3a2a20', x - 0.3, y - 34, 0.6, 8);
  poly(c, '#3a424c', [x - 5.6, y - 34, x + 6, y - 34, x + 2.6, y - 38, x - 2.2, y - 38]);
  if (night) ellipse(c, 'rgba(255,220,150,0.3)', x, y - 30, 8, 8);
}

function railing(c: Ctx, d: Decor): void {
  const w = d.w ?? 200, x0 = d.x - w / 2, y = d.y;
  for (let x = x0; x <= x0 + w; x += 12) {
    block(c, x - 1, y - 11, 2.4, 11, '#6a4428', '#8a5a34');
  }
  rect(c, '#8a5a34', x0, y - 11, w, 1.6);
  rect(c, '#6a4428', x0, y - 6, w, 1.2);
}

function valleyGate(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  for (const px of [x - 28, x + 26]) {
    shadow(c, px + 1, y, 3, 1.2);
    block(c, px, y - 34, 3, 34, '#5a3a20', '#7a5030');
  }
  block(c, x - 34, y - 38, 68, 5, '#4a2e18', '#6a4428');
  block(c, x - 20, y - 33, 40, 11, '#f4ecd8', '#ffffff', '#c8b890');
  txt(c, '地熱谷', x, y - 29, 5.4, '#2a1a10', 900);
  txt(c, 'THERMAL VALLEY', x, y - 24.6, 2.2, '#5a4a3a', 700);
}

// ---------------------------------------------------------------------------
// Objects
// ---------------------------------------------------------------------------

const OBJECTS: Record<string, (c: Ctx, r: Rect, t: number) => void> = {
  ck_mrt(c, r) {
    // A glass MRT entrance pavilion with the red line's colour stripe and a 捷運 sign.
    const { x, y, w, h } = r;
    const by = y + h, cx = x + w / 2;
    shadow(c, cx, by, w / 2 + 3, 3);
    vgrad(c, x + 2, by - 30, w - 4, 30, isNight() ? '#ffeec0' : '#9ac8d8', isNight() ? '#e0b070' : '#4a8098');
    for (let i = x + 2; i < x + w - 2; i += 12) block(c, i, by - 30, 2, 30, '#d8dce0', '#ffffff');
    rect(c, 'rgba(20,30,40,0.55)', cx - 9, by - 22, 18, 22);
    // Escalator glimpsed going down.
    for (let k = 0; k < 5; k++) rect(c, '#8a9096', cx - 7, by - 20 + k * 4, 14, 1);
    block(c, x - 2, by - 36, w + 4, 6, '#f4f5f2', '#ffffff', '#c8ccd0');
    rect(c, '#e2493b', x - 2, by - 31, w + 4, 1.2);
    block(c, cx - 18, by - 48, 36, 11, '#1f6fc0', '#3a8fe0', '#14508e');
    ellipse(c, '#ffffff', cx - 11, by - 42.6, 3.4, 3.4);
    ellipse(c, '#1f6fc0', cx - 11, by - 42.6, 2, 2);
    txt(c, '捷運', cx + 3, by - 44.2, 5.4, '#ffffff', 900);
    txt(c, 'MRT · 中正紀念堂', cx + 3, by - 39.6, 2.2, '#cfe6f2', 700);
    if (isNight()) ellipse(c, 'rgba(120,190,255,0.22)', cx, by - 42, 24, 9);
  },
  ck_gate(c, r) {
    // A painted floor marker: the classic photo spot facing the gate.
    const cx = r.x + r.w / 2, cy = r.y + r.h / 2;
    ellipse(c, 'rgba(255,255,255,0.55)', cx, cy, 12, 4.4);
    ellipse(c, 'rgba(232,192,72,0.8)', cx, cy, 10, 3.4);
    txt(c, '📷', cx, cy - 0.4, 4.4, '#1f3a6e', 900);
  },
  ck_steps(c, r) {
    // A small bronze plaque at the foot of the flight: 89.
    const cx = r.x + r.w / 2 - 4, by = r.y + r.h - 2;
    shadow(c, cx, by, 5, 1.4);
    block(c, cx - 5, by - 10, 10, 10, '#7a5a2a', '#a8803a', '#4a3410');
    txt(c, '89', cx, by - 5.4, 5, '#f6dc80', 900);
  },
  ck_guard(c, r) {
    const cx = r.x + r.w / 2, by = r.y + r.h;
    board(c, cx, by, 34, '儀隊交接', 'CHANGING OF THE GUARD', '#1f3a6e');
    // A clock face showing "on the hour".
    ellipse(c, '#ffffff', cx + 13, by - 34, 3.4, 3.4);
    line(c, '#1b1d24', 0.6, [cx + 13, by - 34, cx + 13, by - 36.6]);
    line(c, '#1b1d24', 0.6, [cx + 13, by - 34, cx + 14.6, by - 34]);
  },
  ck_hall(c, r) {
    const cx = r.x + r.w / 2, by = r.y + r.h;
    shadow(c, cx, by, r.w / 2, 2.4);
    block(c, cx - 18, by - 22, 36, 22, '#e8e4da', '#ffffff', '#c8c2b6');
    block(c, cx - 15, by - 20, 30, 14, '#1f3a6e', '#2a4f90');
    txt(c, '中正紀念堂', cx, by - 15.4, 4.6, L.gold, 900);
    txt(c, 'MEMORIAL HALL', cx, by - 10.4, 2.2, '#cfe6f2', 700);
  },
  ck_theater(c, r) {
    board(c, r.x + r.w / 2, r.y + r.h, 34, '國家戲劇院', 'NATIONAL THEATER', '#8a1f18');
  },
  ck_concert(c, r) {
    board(c, r.x + r.w / 2, r.y + r.h, 34, '國家音樂廳', 'NATIONAL CONCERT HALL', '#8a1f18');
  },
  ck_koi(c, r) {
    // A little fish-food dispenser on a post.
    const cx = r.x + 10, by = r.y + r.h;
    shadow(c, cx, by, 4, 1.4);
    rect(c, '#55606a', cx - 0.7, by - 16, 1.4, 16);
    block(c, cx - 5, by - 26, 10, 11, '#d6302a', '#e85a48', '#8a1f18');
    rect(c, '#ffffff', cx - 3.6, by - 23.6, 7.2, 4);
    txt(c, '魚飼料', cx, by - 21.6, 2.6, '#8a1f18', 900);
    ellipse(c, '#ff7a2a', cx, by - 17.6, 2.4, 1);
  },
  ck_yunhan(c, r) {
    board(c, r.x + r.w / 2, r.y + r.h, 30, '雲漢池', 'YUNHAN POND', '#2f6a5a');
  },
  ck_kite: (c, r, t) =>
    cart(c, r, t, '風箏', 'KITES', '#3a8fd9', (c, x, y, t) => {
      // Kites for sale clipped to the canopy posts.
      for (let k = 0; k < 3; k++) {
        const kx = x + 4 + k * 10, ky = y - 18 + Math.sin(t * 2 + k) * 0.6;
        poly(c, ['#e2493b', '#ffd34e', '#5fbf5a'][k], [kx, ky - 5, kx + 4, ky, kx, ky + 5, kx - 4, ky]);
      }
    }),
  ck_snack: (c, r, t) =>
    cart(c, r, t, '雞蛋糕', 'EGG CAKES', '#e2a03a', (c, x, y, t) => {
      // A cast-iron mould with little golden cakes, and steam.
      block(c, x + 8, y - 6, 26, 6, '#2b2e33', '#4a4e54');
      for (let k = 0; k < 5; k++) ellipse(c, '#e8a83a', x + 11 + k * 5, y - 4, 2, 1.4);
      steam(c, x + 20, y - 7, t, 3, 0.4);
    }),
  ck_bubble: (c, r, t) =>
    cart(c, r, t, '珍珠奶茶', 'BUBBLE TEA', '#7a5030', (c, x, y) => {
      for (let k = 0; k < 4; k++) {
        const cx = x + 8 + k * 9;
        block(c, cx - 2.4, y - 9, 4.8, 9, '#e8d0b0', '#f6e8d0', '#c8a880');
        for (let b = 0; b < 3; b++) ellipse(c, '#2a1a10', cx - 1.2 + b * 1.2, y - 1.6, 0.6, 0.6);
        rect(c, ['#ff6f91', '#3a8fd9', '#5fbf5a', '#ffd34e'][k], cx - 0.3, y - 13, 0.8, 5);
      }
    }),
  ck_corridor(c, r) {
    // A wooden notice board with pinned papers.
    const cx = r.x + r.w / 2, by = r.y + r.h;
    shadow(c, cx, by, 12, 2);
    for (const px of [cx - 12, cx + 10]) block(c, px, by - 18, 2, 18, '#5a3a20', '#7a5030');
    block(c, cx - 14, by - 32, 28, 16, '#8a5a34', '#a8784a', '#5a3a20');
    block(c, cx - 12, by - 30, 10, 12, '#fbf8ef', '#ffffff');
    block(c, cx + 1, by - 30, 11, 6, '#ffe8a0', '#fff4c8');
    block(c, cx + 1, by - 23, 11, 5, '#bfe6ff', '#e0f4ff');
    for (let k = 0; k < 4; k++) rect(c, '#55606a', cx - 11, by - 28 + k * 2.6, 8, 0.5);
    txt(c, '?', cx + 6.6, by - 27, 4, '#c0392b', 900);
    for (const [px2, py2] of [[cx - 7, by - 30], [cx + 6, by - 30], [cx + 6, by - 23]]) ellipse(c, '#e2493b', px2, py2, 0.7, 0.7);
  },
  // --- Beitou -----------------------------------------------------------------------------------
  ck_btMrt(c, r) {
    // Ticket-gate entrance in front of the station hall.
    const { x, y, w, h } = r;
    const by = y + h, cx = x + w / 2;
    shadow(c, cx, by, w / 2, 2.4, 0.25);
    for (let i = 0; i < 4; i++) {
      const gx = x + 4 + i * ((w - 12) / 3);
      block(c, gx, by - 12, 4, 12, '#c8ccd0', '#e8ecf0', '#9aa0a8');
      rect(c, '#4aff8a', gx + 1, by - 10, 2, 1);
    }
    block(c, cx - 16, by - 32, 32, 9, '#1f6fc0', '#3a8fe0', '#14508e');
    rect(c, '#55606a', cx - 0.6, by - 23, 1.2, 11);
    txt(c, '捷運 MRT', cx, by - 27.6, 4.4, '#ffffff', 900);
  },
  ck_oldStation(c, r) {
    board(c, r.x + r.w / 2, r.y + r.h, 32, '新北投車站', 'SINCE 1916', '#5a3a20', '#f4ecd8');
  },
  ck_bath(c, r, t) {
    // The Millennium Hot Spring's gatehouse: a wooden ticket window under a tiled roof, with a noren.
    const { x, y, w, h } = r;
    const by = y + h, cx = x + w / 2;
    shadow(c, cx, by, w / 2 + 3, 3);
    boards(c, x, by - 26, w, 26, '#c8a878');
    windowGrid(c, x + 5, by - 22, 16, 12, isNight(), '#5a3a20');
    block(c, x + 4, by - 10, 18, 2, '#7a5030', '#9a6a40');
    // Noren curtain with ♨ 千禧湯.
    block(c, cx + 2, by - 24, 30, 13, '#1f3a6e', '#2a4f90');
    for (let k = 1; k < 4; k++) rect(c, 'rgba(255,255,255,0.4)', cx + 2 + k * 7.5, by - 24, 0.5, 13);
    txt(c, '♨', cx + 8, by - 17.6, 6, '#ffffff', 900);
    txt(c, '千禧湯', cx + 22, by - 17.6, 4.4, '#ffffff', 900);
    japRoof(c, x - 2, w + 4, by - 26, 16, '#3a424c', true);
    steam(c, x + w - 6, by - 44, t, 3, 0.3, 16);
  },
  ck_footbath(c, r, t) {
    // A long stone foot bath with steaming water and a wooden bench edge.
    const { x, y, w, h } = r;
    const by = y + h;
    shadow(c, x + w / 2, by, w / 2 + 3, 3, 0.25);
    block(c, x, y, w, h, '#8a8a84', '#a8a8a0', '#6a6a64');
    vgrad(c, x + 3, y + 3, w - 6, h - 8, '#8ac8b8', '#4a9a8a');
    for (let k = 0; k < 4; k++) {
      const u = (t * 0.25 + k / 4) % 1;
      rect(c, `rgba(255,255,255,${0.35 * (1 - u)})`, x + 6 + ((k * 23 + u * 30) % (w - 16)), y + 5 + (k % 2) * 4, 6, 0.6);
    }
    // Wooden seat along the front edge.
    block(c, x - 2, by - 5, w + 4, 5, '#8a5a34', '#a8784a', '#5a3a20');
    for (let i = x + 6; i < x + w; i += 16) rect(c, 'rgba(0,0,0,0.2)', i, by - 5, 0.6, 5);
    // Feet in the water (pairs of little pink feet).
    for (const fx of [x + 14, x + 40, x + 74]) {
      ellipse(c, '#f0b098', fx, y + 9 + Math.sin(t + fx) * 0.4, 1.6, 2.4);
      ellipse(c, '#f0b098', fx + 4, y + 9 + Math.cos(t + fx) * 0.4, 1.6, 2.4);
    }
    steam(c, x + 20, y + 4, t, 3, 0.35, 14);
    steam(c, x + 60, y + 4, t + 0.5, 3, 0.35, 14);
    board(c, x + w + 10, by, 26, '泡腳池', 'FREE FOOT BATH', '#2f6a5a');
  },
  ck_valley(c, r) {
    board(c, r.x + r.w / 2, r.y + r.h, 32, '地熱谷', 'THERMAL VALLEY 90°C', '#5a3a20', '#f4ecd8');
  },
  ck_eggs: (c, r, t) =>
    cart(c, r, t, '溫泉蛋', 'HOT-SPRING EGGS', '#2f8a7a', (c, x, y, t) => {
      // A basket of eggs sitting in a steaming pot.
      block(c, x + 6, y - 9, 22, 9, '#8a9096', '#b8c0c8', '#5a6068');
      ellipse(c, '#a8d8c0', x + 17, y - 9, 10, 2);
      for (let k = 0; k < 5; k++) ellipse(c, '#f8f0e0', x + 10 + k * 3.6, y - 10.4 + (k % 2) * 0.6, 1.6, 1.2);
      steam(c, x + 17, y - 11, t, 3, 0.45, 14);
      for (let k = 0; k < 3; k++) ellipse(c, '#e8dcc0', x + 32 + k * 3.4, y - 2, 1.5, 1.1);
    }),
  ck_plum(c, r) {
    board(c, r.x + r.w / 2, r.y + r.h, 30, '梅庭', 'PLUM GARDEN', '#2a1a10', '#f4ecd8');
  },
  ck_stream(c, r) {
    const cx = r.x + r.w / 2, by = r.y + r.h;
    shadow(c, cx, by, 4, 1.4);
    rect(c, '#5a3a20', cx - 0.7, by - 16, 1.6, 16);
    block(c, cx - 11, by - 28, 22, 12, '#f4ecd8', '#ffffff', '#c8b890');
    txt(c, '北投溪', cx, by - 24, 4.4, '#2a1a10', 900);
    txt(c, 'WARM WATER', cx, by - 19.6, 2.2, '#5a4a3a', 700);
  },
};

// ---------------------------------------------------------------------------
// Decor dispatcher
// ---------------------------------------------------------------------------

function drawDecor(c: Ctx, d: Decor, t: number): boolean {
  if (!d.kind.startsWith('ck_')) return false;
  const night = isNight();
  switch (d.kind) {
    case 'ck_gate':
      cachedArt(c, `ckGate:${night ? 'n' : 'd'}`, d.x - 180, d.y - 136, 360, 144, (cc) => {
        cc.translate(d.x, d.y);
        cc.scale(1.2, 1.2);
        cc.translate(-d.x, -d.y);
        gateArt(cc, d.x, d.y, night);
      });
      return true;
    case 'ck_hall':
      drawHallSlice(c, d, t);
      return true;
    case 'ck_stairs':
      cachedArt(c, 'ckStairs', 1012, 370, 210, 180, (cc) => stairsArt(cc));
      climbers(c, t);
      return true;
    case 'ck_pavilion':
      cachedArt(c, `ckPav:${d.x},${d.y}`, d.x - 34, d.y - 60, 68, 66, (cc) => pavilion(cc, d.x, d.y));
      return true;
    case 'ck_theater': {
      const v = d.variant ?? 0;
      cachedArt(c, `ckPalace${v}:${night ? 'n' : 'd'}`, d.x - 180, d.y - 150, 360, 158, (cc) => palaceArt(cc, d.x, d.y, v === 1, night));
      return true;
    }
    case 'ck_wall':
      perimeterWall(c, d);
      return true;
    case 'ck_corridor':
      corridor(c, d, t);
      return true;
    case 'ck_sign': {
      const [zh, en] = SIGNS[(d.variant ?? 0) % SIGNS.length];
      board(c, d.x, d.y, 34, zh, en, '#2f6a5a');
      return true;
    }
    case 'ck_bridge': {
      // Red wooden garden railings along both sides of a pond bridge (the bridge runs north–south).
      const h = d.h ?? 140;
      for (const rx of [d.x - 16, d.x + 15]) {
        for (let y = d.y - h; y <= d.y; y += 10) block(c, rx - 1, y - 9, 2.4, 9, '#b8322a', '#d8503e', '#8a1f18');
        rect(c, '#b8322a', rx - 0.8, d.y - h - 9, 2, h);
        rect(c, '#d8503e', rx - 0.8, d.y - h - 9, 0.8, h);
      }
      return true;
    }
    case 'ck_lamp':
      palaceLamp(c, d.x, d.y);
      return true;
    case 'ck_koi':
      koiSchool(c, d, t);
      return true;
    case 'ck_lotus':
      lotus(c, d);
      return true;
    case 'ck_pigeons':
      pigeonFlock(c, d, t);
      return true;
    case 'ck_kites':
      kites(c, d, t);
      return true;
    case 'ck_taichi':
      taichiGroup(c, d, t);
      return true;
    case 'ck_dancers':
      danceGroup(c, d, t);
      return true;
    case 'ck_school':
      schoolGroup(c, d, t);
      return true;
    case 'ck_tourists':
      touristGroup(c, d, t);
      return true;
    case 'ck_balloons':
      balloonSeller(c, d, t);
      return true;
    // --- Beitou ---
    case 'ck_mrtStation':
      cachedArt(c, `ckMrtSt:${night ? 'n' : 'd'}`, d.x - 124, d.y - 112, 248, 118, (cc) => mrtStationArt(cc, d.x, d.y, night));
      return true;
    case 'ck_oldStation':
      cachedArt(c, `ckOldSt:${night ? 'n' : 'd'}`, d.x - 104, d.y - 92, 208, 98, (cc) => oldStationArt(cc, d.x, d.y, night));
      return true;
    case 'ck_library':
      cachedArt(c, `ckLib:${night ? 'n' : 'd'}`, d.x - 132, d.y - 128, 264, 134, (cc) => libraryArt(cc, d.x, d.y, night));
      return true;
    case 'ck_museum':
      cachedArt(c, `ckMus:${night ? 'n' : 'd'}`, d.x - 104, d.y - 104, 208, 110, (cc) => museumArt(cc, d.x, d.y, night));
      return true;
    case 'ck_plum':
      cachedArt(c, `ckPlum:${night ? 'n' : 'd'}`, d.x - 66, d.y - 70, 132, 76, (cc) => plumArt(cc, d.x, d.y, night));
      return true;
    case 'ck_hotel': {
      const v = d.variant ?? 0;
      cachedArt(c, `ckHotel${v}:${d.x},${d.y}:${night ? 'n' : 'd'}`, d.x - 68, d.y - 90, 136, 96, (cc) => hotelArt(cc, d.x, d.y, v, night));
      return true;
    }
    case 'ck_bathers':
      bathers(c, d, t);
      return true;
    case 'ck_steam':
      steamPuffs(c, d, t);
      return true;
    case 'ck_rock':
      rock(c, d);
      return true;
    case 'ck_danger':
      dangerSign(c, d);
      return true;
    case 'ck_btLamp':
      btLamp(c, d.x, d.y);
      return true;
    case 'ck_railing':
      railing(c, d);
      return true;
    case 'ck_valleyGate':
      valleyGate(c, d);
      return true;
    case 'ck_lanterns':
      lanternRow(c, d, t);
      return true;
    default:
      return false;
  }
}

registerPropDrawers({
  objects: OBJECTS,
  decor: drawDecor,
  animatedObjects: ['ck_snack', 'ck_kite', 'ck_footbath', 'ck_eggs', 'ck_bath'],
  animatedDecor: ['ck_hall', 'ck_koi', 'ck_pigeons', 'ck_kites', 'ck_taichi', 'ck_dancers', 'ck_school', 'ck_tourists', 'ck_balloons', 'ck_stairs', 'ck_corridor', 'ck_bathers', 'ck_steam', 'ck_lanterns'],
});
